import { useEffect, useRef, useState } from 'react'
import type {
  ChecklistItem as ChecklistItemT,
  ChecklistSection as ChecklistSectionT,
  ChecklistStatus,
  DueDiligenceChecklist,
  ExecutiveSummary,
  FeasibilityReport,
  FeasibilityRequest,
  FormComparison,
  FormOutcome,
  FormOutlook,
  Outcomes,
  ValueT,
} from '../api'
import { consultantPhotoUrl, getAnalystStatus, pdfUrl } from '../api'
import { AskReport } from './AskReport'
import { ClauseSnippet } from './ClauseSnippet'
import { MapView } from './MapView'
import { ValueCell } from './ValueCell'

type ValueOverlay = ValueT & { status?: 'value' | 'not_affected' | 'not_on_map' | 'unavailable' }

// Section B rows we keep in the payload (admins / debug) but don't render
// to the client because they're informational duplicates or unreliable.
const B_HIDDEN_KEYS = new Set([
  'land_application',  // already shown in Section A as "Applicable LEP"
  'zone_label',        // raw LAY_CLASS — confusing strings like "Included"
  'heritage_class',    // duplicate of heritage; merged into one row visually
])

// Plain-English labels + one-line definitions + display unit so each
// Section C row shows the right unit (60 -> "60 %", 12 -> "12 m", etc.)
// without the reader having to recognise the field name suffix.
const CONTROL_LABELS: Record<string, { label: string; help?: string; unit?: string }> = {
  max_built_upon_area_pct: {
    label: 'Max built-upon area',
    help: 'Buildings + driveways + paths + impervious surfaces as % of site area.',
    unit: '%',
  },
  max_site_coverage_pct: {
    label: 'Max site coverage',
    help: 'Building footprint as % of site area (buildings only).',
    unit: '%',
  },
  min_landscaped_area_pct: {
    label: 'Min landscaped area',
    help: 'Vegetated / permeable area as % of site area.',
    unit: '%',
  },
  min_deep_soil_pct: {
    label: 'Min deep soil',
    help: 'Portion of landscaped area kept unbuilt above & below — for tree planting.',
    unit: '%',
  },
  min_principal_pos_sqm: {
    label: 'Min principal private open space',
    help: 'Primary outdoor living area, minimum m².',
    unit: 'm²',
  },
  front_setback_m: { label: 'Front setback', help: 'Minimum distance from the front lot boundary to the building façade.' },
  side_setback_m: { label: 'Side setback', help: 'Minimum distance from each side boundary; usually different for ground vs upper storeys.' },
  rear_setback_m: { label: 'Rear setback', help: 'Minimum distance from the rear boundary; usually larger on the upper storey.' },
  max_floor_plate_upper_pct: {
    label: 'Max upper floor plate',
    help: 'Upper-storey footprint as % of ground-storey footprint — caps the bulk of the first floor.',
    unit: '%',
  },
  max_storeys: { label: 'Max storeys', help: 'Maximum number of habitable storeys above natural ground level.' },
  max_wall_height_m: { label: 'Max wall height', help: 'Distance from natural ground level to the top of the external wall.', unit: 'm' },
  max_ridge_height_m: { label: 'Max ridge height', help: 'Distance from natural ground level to the highest point of the roof.', unit: 'm' },
  articulation_zone_m: {
    label: 'Articulation zone',
    help: 'Permitted projection into the front setback for articulation elements (porches, bays).',
    unit: 'm',
  },
  solar_access: {
    label: 'Solar access',
    help: 'Required midwinter solar hours to principal private open space.',
  },
}

export function Report({
  report,
  docsPending = false,
  certPending = false,
  contractPending = false,
  onRerun,
}: {
  report: FeasibilityReport
  docsPending?: boolean
  // True when the s10.7 review job is still in flight — cert-driven
  // overlay rows render a loading state instead of "Verify with s10.7
  // certificate" so the planner knows their upload is being read.
  certPending?: boolean
  // True when the contract-of-sale review job is still in flight —
  // Section I (Title encumbrances) shows a small reviewing banner so
  // the planner doesn't read an empty list as "no encumbrances".
  contractPending?: boolean
  // Re-run the feasibility with edited dimension overrides from
  // Section A. App owns the loading state — once invoked, the report
  // unmounts and the global LoadingProgress takes over.
  onRerun?: (overrides: Partial<FeasibilityRequest>) => Promise<void> | void
}) {
  const A = report.sections.A_property_summary
  const B = report.sections.B_planning_controls
  const C = report.sections.C_council_pathway
  const D = report.sections.D_cdc_pathway
  const E = report.sections.E_pathway_comparison
  const F = report.sections.F_citations
  const lga = A.lga_slug as string | null
  // Combined single-dwelling + duplex run — one block per form. Null
  // for single-form runs, where every section renders as before. The
  // flat C / D / E / cdc_verdict hold the primary (single dwelling);
  // Section J comes from the duplex block.
  const L = ((report.sections as any).L_form_comparison ?? null) as FormComparison | null
  const duplexBlock = L?.forms.find((b) => b.housing_form.startsWith('dual_occupancy')) ?? null

  // Live analyst state — polled from the background task while it
  // runs. Seeded from the initial response so a report that already
  // has a terminal status (cached, or no API key) doesn't trigger a
  // pointless first poll.
  const analystLive = useAnalystStatus(report.id, {
    analyst: (report as any).analyst,
    status: (report as any).analyst_status,
    failureReason: (report as any).analyst_failure_reason,
  })
  // Block PDF export while the analyst is still computing — the PDF
  // would otherwise bake the "Running findings analyst…" skeleton
  // into a permanent file and the planner would have a half-done
  // report. Same rationale as the cert / contract review gate.
  const analystPending = analystLive.status === 'running'
  const pdfDisabledReason = docsPending
    ? 'available once your documents finish reviewing'
    : analystPending
    ? 'available once the findings analyst finishes'
    : null

  return (
    <article className="mt-8 space-y-10">
      <div className="flex items-end justify-between border-b border-massing-rule pb-4">
        <div>
          <div className="brand-eyebrow mb-1">Feasibility report</div>
          <h1 className="font-serif text-3xl text-massing-ink">{report.address}</h1>
        </div>
        {pdfDisabledReason ? (
          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              disabled
              className="bg-massing/40 text-massing-cream/60 px-5 py-2.5 text-sm tracking-wide cursor-not-allowed"
              title={pdfDisabledReason}
            >
              Download PDF
            </button>
            <span className="text-[11px] text-massing-ink-light italic">
              {pdfDisabledReason}
            </span>
          </div>
        ) : (
          <a
            href={pdfUrl(report.id)}
            className="bg-massing hover:bg-massing-deep text-massing-cream px-5 py-2.5 text-sm tracking-wide transition-colors"
          >
            Download PDF
          </a>
        )}
      </div>

      <CachedDocumentsBanner
        cached={(report as any).cached_documents}
        onApply={() => onRerun?.({ apply_uploaded_docs: true })}
      />

      {/* Sticky right-edge table of contents. Hidden below the `lg`
          breakpoint (1024 px) — on narrower screens the report
          already wraps and a side rail would compete with content. */}
      <ReportTOC
        report={report}
        analystStatus={analystLive.status ?? null}
        hasExecutiveSummary={Boolean(analystLive.analyst?.executive_summary)}
      />

      {/* Executive summary + "read this first" disclaimer — the
          client-facing page. Always mounted (the disclaimer is the
          load-bearing part); the summary content renders once the
          analyst has produced it. */}
      <div id="sec-summary" className="scroll-mt-24">
        <ExecutiveSummaryCard
          summary={analystLive.analyst?.executive_summary ?? null}
          status={analystLive.status ?? null}
          disclaimer={(report as any).client_disclaimer ?? null}
          outcomes={(report as any).outcomes ?? null}
        />
      </div>

      {/* Ask-the-report helper — free-form questions answered from
          this report + the instrument text. Opens once the analyst
          has finished (the backend refuses earlier). */}
      <div id="sec-ask" className="scroll-mt-24">
        <AskReport reportId={report.id} ready={!analystPending} />
      </div>

      <section id="sec-a" className="scroll-mt-24">
        <SectionHeader>A. Property summary</SectionHeader>
        <PropertySummaryCard A={A} onRerun={onRerun} />
      </section>

      <div id="sec-analyst" className="scroll-mt-24">
        <FindingsAnalysis
          analyst={analystLive.analyst}
          status={analystLive.status}
          failureReason={analystLive.failureReason}
          elapsedSec={analystLive.elapsedSec}
        />
      </div>

      {L ? (
        <>
          {/* Combined run — side-by-side comparison first, then each
              form's own next steps. Section J reads the duplex block
              (the flat J keys hold the single-dwelling primary = null). */}
          <div id="sec-verdict" className="scroll-mt-24">
            <FormComparisonHero comparison={L} />
          </div>

          <div id="sec-next-steps" className="scroll-mt-24 space-y-8">
            {L.forms.map((blk) => (
              <FormSubBlock
                key={blk.housing_form}
                label={blk.label}
                sublabel={blk.sublabel}
                framed={false}
              >
                <NextStepsCard verdict={blk.cdc_verdict} />
              </FormSubBlock>
            ))}
          </div>

          <DualOccupancySection
            comparison={duplexBlock?.J_dual_occupancy_comparison}
            single={duplexBlock?.J_dual_occupancy_assessment}
          />
        </>
      ) : (
        <>
          <div id="sec-verdict" className="scroll-mt-24">
            <CDCVerdictCard verdict={(report as any).cdc_verdict} />
          </div>

          <div id="sec-next-steps" className="scroll-mt-24">
            <NextStepsCard verdict={(report as any).cdc_verdict} />
          </div>

          <DualOccupancySection
            comparison={(report.sections as any).J_dual_occupancy_comparison}
            single={(report.sections as any).J_dual_occupancy_assessment}
          />
        </>
      )}

      <section id="sec-b" className="scroll-mt-24">
        <SectionHeader>B. Planning controls</SectionHeader>
        {(() => {
          const cert = (report.sections as any).H_planning_certificate
          if (!cert) return null
          return (
            <div className="mb-4 px-4 py-3 bg-massing-cream border border-massing-rule text-[12px] text-massing-ink-mid">
              <div className="flex items-baseline gap-2 mb-0.5">
                <span className="brand-eyebrow text-massing">Section 10.7 certificate</span>
                {cert.confidence && (
                  <span className="text-massing-ink-light">· {cert.confidence} confidence</span>
                )}
              </div>
              <div>
                {cert.council_name ?? 'Council'}
                {cert.cert_number && <> · cert <strong>{cert.cert_number}</strong></>}
                {cert.issued_at && <> · issued {cert.issued_at}</>}
              </div>
              <div className="text-[11px] text-massing-ink-light italic mt-0.5">
                Overlay rows tagged <em>via s10.7</em> use council's own finding instead of the spatial-layer query.
              </div>
            </div>
          )
        })()}
        <table className="w-full text-sm">
          <tbody>
            {Object.entries(B.controls as Record<string, ValueOverlay>)
              .filter(([k]) => !B_HIDDEN_KEYS.has(k))
              .map(([k, v]) => {
                // For FSR / Height: if the LEP clause-resolution returned
                // a different value to the raw map, show "map → resolved"
                // so the reader doesn't see one number here and another
                // in Section C and wonder which applies.
                let resolvedHint: { value: unknown; citation?: string } | null = null
                if (k === 'fsr') {
                  const r = (C.resolved_lep?.fsr ?? null) as ValueT | null
                  if (r && r.value != null && r.value !== v.value) {
                    resolvedHint = { value: r.value, citation: r.citation ?? undefined }
                  }
                } else if (k === 'height_of_buildings') {
                  const r = (C.resolved_lep?.height_m ?? null) as ValueT | null
                  if (r && r.value != null && r.value !== v.value) {
                    resolvedHint = { value: r.value, citation: r.citation ?? undefined }
                  }
                }
                const certApplies = (report.sections as any).H_planning_certificate != null
                return <OverlayRow key={k} controlKey={k} v={v} resolvedHint={resolvedHint} certApplies={certApplies} certPending={certPending} />
              })}
          </tbody>
        </table>
      </section>

      <section id="sec-c" className="scroll-mt-24">
        <SectionHeader>C. Council DA pathway</SectionHeader>
        {L ? (
          <>
            <p className="text-xs text-massing-ink-light italic mb-4 max-w-3xl">
              Two housing forms were assessed on this lot — one block per form below.
              Council controls that vary by form (minimum lot, setbacks, site coverage)
              are resolved separately for each.
            </p>
            {L.forms.map((blk) => (
              <FormSubBlock
                key={blk.housing_form}
                id={`sec-c-${blk.housing_form}`}
                label={blk.label}
                sublabel={blk.sublabel}
              >
                <CouncilPathwayBody C={blk.C_council_pathway} A={A} lga={lga} />
              </FormSubBlock>
            ))}
          </>
        ) : (
          <CouncilPathwayBody C={C} A={A} lga={lga} />
        )}
      </section>

      {/* Section D is the CDC controls / GFA breakdown. When CDC is
          blocked (the headline verdict above already says so), the
          body collapses behind a disclosure — see CdcPathwayBody. */}
      <section id="sec-d" className="scroll-mt-24">
        <SectionHeader>D. CDC pathway (Codes SEPP)</SectionHeader>
        {L ? (
          L.forms.map((blk) => (
            <FormSubBlock
              key={blk.housing_form}
              id={`sec-d-${blk.housing_form}`}
              label={blk.label}
              sublabel={blk.sublabel}
            >
              <CdcPathwayBody D={blk.D_cdc_pathway} cdcBlocked={blk.cdc_verdict?.status === 'no'} />
            </FormSubBlock>
          ))
        ) : (
          <CdcPathwayBody D={D} cdcBlocked={((report as any).cdc_verdict?.status) === 'no'} />
        )}
      </section>

      <section id="sec-e" className="scroll-mt-24">
        <SectionHeader>E. Pathway comparison</SectionHeader>
        {L ? (
          L.forms.map((blk) => (
            <FormSubBlock
              key={blk.housing_form}
              id={`sec-e-${blk.housing_form}`}
              label={blk.label}
              sublabel={blk.sublabel}
            >
              <PathwayComparisonBody E={blk.E_pathway_comparison} />
            </FormSubBlock>
          ))
        ) : (
          <PathwayComparisonBody E={E} />
        )}
      </section>

      <section id="sec-f" className="scroll-mt-24">
        <SectionHeader>F. Citations</SectionHeader>
        <table className="w-full text-xs">
          <thead>
            <tr><Th>Source</Th><Th>Citation</Th><Th>Layer</Th></tr>
          </thead>
          <tbody>
            {(F as any[]).map((c: any, i: number) => (
              <tr key={i} className="border-t border-massing-rule-soft">
                <td className="py-1.5 pr-3">{c.source}</td>
                <td className="py-1.5 pr-3">{c.citation}</td>
                <td className="py-1.5 pr-3">{c.layer}{c.layer_version ? ` (${c.layer_version})` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {Array.isArray((report.sections as any).G_clauses_appendix) && (report.sections as any).G_clauses_appendix.length > 0 && (
        <section id="sec-g" className="scroll-mt-24">
          <SectionHeader>G. Clauses appendix</SectionHeader>
          <p className="text-xs text-massing-ink-light italic mb-4 max-w-3xl">
            Verbatim extracts of every LEP / DCP / SEPP clause cited above.
            Punctuation and wording match the source as parsed — use these
            to verify the numbers in their original context.
          </p>
          <div className="space-y-5">
            {((report.sections as any).G_clauses_appendix as any[]).map((c, i) => (
              <ClauseExtract
                key={i}
                source={c.source}
                citation={c.citation}
                text={c.source_text}
              />
            ))}
          </div>
        </section>
      )}

      {(() => {
        const contract = (report.sections as any).I_contract_of_sale
        if (!contract) {
          // No contract section yet — render an explicit "still
          // reviewing" placeholder when the contract job is in flight
          // so the planner can see where the encumbrances will land
          // rather than missing the section entirely.
          if (!contractPending) return null
          return (
            <section id="sec-i" className="scroll-mt-24">
              <SectionHeader>I. Title encumbrances</SectionHeader>
              <div className="rounded-md border border-massing-rule-soft bg-massing-cream/30 px-4 py-3 text-sm text-massing-ink-mid italic flex items-center gap-2.5">
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full brand-gradient-bg animate-pulse"
                  aria-hidden
                />
                Reading your contract of sale — easements and covenants will appear here the moment the review finishes.
              </div>
            </section>
          )
        }
        const encumbrances = (contract.encumbrances ?? []) as Array<{
          type: string; label: string; instrument: string | null;
          description: string | null; burden: string | null; benefit: string | null;
          affected_area_m2: number | null; fsr_limit: number | null;
          height_limit_m: number | null; max_dwellings: number | null;
          source_text: string | null;
        }>
        const specials = (contract.special_conditions ?? []) as Array<{ summary: string; source_text: string }>
        return (
          <section id="sec-i" className="scroll-mt-24">
            <SectionHeader>I. Title encumbrances</SectionHeader>
            <p className="text-xs text-massing-ink-light italic mb-3 max-w-3xl">
              Easements, restrictive covenants and other items registered on
              title via the s88B instrument. Easements physically reduce the
              buildable footprint; restrictive covenants override the LEP / DCP
              where they're stricter.
            </p>
            <p className="text-[11px] text-massing-ink-mid mb-4">
              <span className="font-medium">Contract of sale</span>
              {contract.contract_date && <> · dated {contract.contract_date}</>}
              {contract.confidence && <> · {contract.confidence} confidence</>}
              {contract.parsed_at && <> · parsed {contract.parsed_at.replace('T', ' ').slice(0, 16)}</>}
            </p>
            {encumbrances.length === 0 ? (
              <p className="text-[12px] text-massing-ink-light italic">No registered encumbrances captured by the parser.</p>
            ) : (
              <div className="space-y-3">
                {encumbrances.map((e, i) => (
                  <EncumbranceCard key={i} e={e} />
                ))}
              </div>
            )}
            {(contract.covenant_fsr_min || contract.covenant_height_min || contract.covenant_max_dwellings) && (
              <div className="mt-4 p-3 border-l-2 border-massing-maroon bg-massing-maroon-soft text-[12px]">
                <div className="brand-eyebrow text-massing-maroon mb-1">Binding from covenants</div>
                {contract.covenant_fsr_min != null && (
                  <div>FSR capped at <strong>{contract.covenant_fsr_min}:1</strong> by 88B covenant.</div>
                )}
                {contract.covenant_height_min != null && (
                  <div>Height capped at <strong>{contract.covenant_height_min} m</strong> by 88B covenant.</div>
                )}
                {contract.covenant_max_dwellings != null && (
                  <div>Dwellings capped at <strong>{contract.covenant_max_dwellings}</strong> by 88B covenant.</div>
                )}
              </div>
            )}
            {specials.length > 0 && (
              <div className="mt-6">
                <SubHeader>Special conditions</SubHeader>
                <div className="space-y-3 mt-2">
                  {specials.map((c, i) => (
                    <div key={i}>
                      <div className="text-sm text-massing-ink mb-1.5">{c.summary}</div>
                      {c.source_text && (
                        <ClauseSnippet
                          text={c.source_text}
                          source="Contract of sale — special conditions"
                          truncate={500}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )
      })()}

      {report.sections.K_due_diligence_checklist && (
        <div id="sec-k" className="scroll-mt-24">
          <ChecklistPanel checklist={report.sections.K_due_diligence_checklist} />
        </div>
      )}

      {(report as any).prepared_by && (
        <PreparedByCard preparedBy={(report as any).prepared_by} />
      )}

      <MethodNote />
    </article>
  )
}

// --------------------------------------------------------------------
// Sticky right-edge table of contents. Tracks which section is in
// view via IntersectionObserver and highlights the matching row.
// Clicking a row scrolls to the section (native `<a href="#id">`
// behaviour, paired with `scroll-mt-24` on each section so the
// landing point sits below the sticky header).
//
// Mirrors the PDF's TOC entries — same set, same labels, same
// gating on conditional sections (analyst, dual-occ, I, K). Hidden
// below the `lg` breakpoint (1024 px) because on narrow viewports
// the report already wraps and the side rail would compete with
// content the planner came here to read.
// --------------------------------------------------------------------

interface TOCEntry {
  id: string
  label: string
}

function ReportTOC({
  report,
  analystStatus,
  hasExecutiveSummary = false,
}: {
  report: FeasibilityReport
  analystStatus: string | null
  hasExecutiveSummary?: boolean
}) {
  // The summary page always exists (it carries the disclaimer), so
  // it's always first in the rail; the label just tightens when the
  // analyst hasn't written the summary yet.
  const entries: TOCEntry[] = [
    {
      id: 'sec-summary',
      label: hasExecutiveSummary ? 'Executive summary' : 'Read this first',
    },
    { id: 'sec-ask', label: 'Ask about this report' },
    { id: 'sec-a', label: 'A. Property summary' },
  ]
  if (analystStatus || (report as any).analyst) {
    entries.push({ id: 'sec-analyst', label: 'Findings analysis' })
  }
  const tocL = (report.sections as any).L_form_comparison as FormComparison | null | undefined
  if (tocL) {
    entries.push({ id: 'sec-verdict', label: 'Verdict — both forms' })
    if (tocL.forms.some((b) => (b.cdc_verdict?.next_steps ?? []).length > 0)) {
      entries.push({ id: 'sec-next-steps', label: 'What to do next' })
    }
  } else if ((report as any).cdc_verdict) {
    entries.push({ id: 'sec-verdict', label: 'CDC verdict' })
    if (((report as any).cdc_verdict.next_steps ?? []).length > 0) {
      entries.push({ id: 'sec-next-steps', label: 'What to do next' })
    }
  }
  entries.push(
    { id: 'sec-b', label: 'B. Planning controls' },
    { id: 'sec-c', label: 'C. Council DA pathway' },
    { id: 'sec-d', label: 'D. CDC pathway' },
    { id: 'sec-e', label: 'E. Pathway comparison' },
    { id: 'sec-f', label: 'F. Citations' },
  )
  if (
    Array.isArray((report.sections as any).G_clauses_appendix)
    && (report.sections as any).G_clauses_appendix.length > 0
  ) {
    entries.push({ id: 'sec-g', label: 'G. Clauses appendix' })
  }
  if ((report.sections as any).I_contract_of_sale) {
    entries.push({ id: 'sec-i', label: 'I. Title encumbrances' })
  }
  if (report.sections.K_due_diligence_checklist) {
    entries.push({ id: 'sec-k', label: 'K. Due-diligence checklist' })
  }

  const [activeId, setActiveId] = useState<string>(entries[0]?.id ?? '')

  useEffect(() => {
    // IntersectionObserver fires whenever a section enters or leaves
    // the viewport. We want the LAST section whose top has crossed
    // ~30% of the viewport from the top — i.e. the section the
    // planner is currently reading. Tracking that via a Set of
    // currently-intersecting ids + picking the topmost one each tick.
    const visible = new Set<string>()

    const observer = new IntersectionObserver(
      (records) => {
        for (const r of records) {
          if (r.isIntersecting) visible.add(r.target.id)
          else visible.delete(r.target.id)
        }
        // Topmost visible entry wins — that's the section whose
        // heading the planner has most recently passed under.
        const visibleInOrder = entries
          .map((e) => e.id)
          .filter((id) => visible.has(id))
        if (visibleInOrder.length > 0) {
          setActiveId(visibleInOrder[0])
        }
      },
      {
        // Trigger when the top of the section crosses 25% from the
        // top of the viewport, and stops triggering once it's
        // crossed 60% from the bottom — gives a comfortable
        // "you're reading this section" window.
        rootMargin: '-25% 0px -60% 0px',
        threshold: 0,
      },
    )

    for (const e of entries) {
      const el = document.getElementById(e.id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
    // entries is rebuilt on every render but the section ids are
    // stable for a given report — depending on report.id keeps the
    // effect from re-running on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.id])

  function jumpTo(id: string, e: React.MouseEvent) {
    e.preventDefault()
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      // Briefly nudge the active state so clicking always feels
      // responsive even before IntersectionObserver catches up.
      setActiveId(id)
    }
  }

  return (
    <nav
      aria-label="On this page"
      className="hidden lg:block fixed top-32 right-6 w-[180px] z-[5]"
    >
      <div className="brand-eyebrow text-massing-ink-light mb-2.5">
        On this page
      </div>
      <ul className="space-y-0.5 border-l border-massing-rule">
        {entries.map((e) => {
          const active = e.id === activeId
          return (
            <li key={e.id}>
              <a
                href={`#${e.id}`}
                onClick={(ev) => jumpTo(e.id, ev)}
                className={
                  'block pl-3 pr-2 py-1 text-[11.5px] leading-tight no-underline transition-colors border-l-2 -ml-px ' +
                  (active
                    ? 'border-l-massing text-massing-ink font-medium'
                    : 'border-l-transparent text-massing-ink-light hover:text-massing-ink')
                }
              >
                {e.label}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

// Visual treatment for each checklist status. Border + background +
// marker glyph all keyed off the same status string so verified rows
// look pleasantly tidy, manual rows look like the to-do list they
// actually are, and blocking rows demand attention.
const CHECKLIST_STATUS_STYLE: Record<
  ChecklistStatus,
  { border: string; bg: string; marker: string; markerCls: string; pill: string; pillCls: string }
> = {
  verified: {
    border: 'border-l-flag-green',
    bg: 'bg-flag-green/5',
    marker: '✓',
    markerCls: 'text-flag-green',
    pill: 'Verified',
    pillCls: 'bg-flag-green/10 text-flag-green border-flag-green/30',
  },
  manual: {
    border: 'border-l-flag-amber',
    bg: 'bg-flag-amber/5',
    marker: '☐',
    markerCls: 'text-flag-amber',
    pill: 'Needs manual check',
    pillCls: 'bg-flag-amber/10 text-flag-amber border-flag-amber/30',
  },
  blocking: {
    border: 'border-l-flag-red',
    bg: 'bg-flag-red/5',
    marker: '✗',
    markerCls: 'text-flag-red',
    pill: 'Blocking',
    pillCls: 'bg-flag-red/10 text-flag-red border-flag-red/30',
  },
  not_applicable: {
    border: 'border-l-massing-rule',
    bg: 'bg-massing-rule-soft/40',
    marker: '—',
    markerCls: 'text-massing-ink-light',
    pill: 'Not applicable',
    pillCls: 'bg-massing-rule-soft text-massing-ink-light border-massing-rule',
  },
}

// The end-of-report due-diligence checklist. Mirrors the K section in
// the PDF so the planner sees the same items in both surfaces. Reads
// from the K_due_diligence_checklist payload built in
// nsw_feasibility/reports/checklist.py — keep visual decisions here,
// but the *items* themselves (and their auto-tick logic) live server-
// side so they can't drift between PDF and web.
function ChecklistPanel({ checklist }: { checklist: DueDiligenceChecklist }) {
  const [filter, setFilter] = useState<'all' | ChecklistStatus>('all')

  const summary = checklist.summary
  const filteredSections: ChecklistSectionT[] = checklist.sections
    .map((section) => ({
      ...section,
      items: filter === 'all'
        ? section.items
        : section.items.filter((it) => it.status === filter),
    }))
    .filter((section) => section.items.length > 0)

  return (
    <section className="mt-10 pt-6 border-t border-massing-rule-soft">
      <div className="brand-eyebrow text-massing-ink-light mb-2">
        K. Due-diligence checklist
      </div>
      <h2 className="font-serif text-2xl text-massing-ink mb-2 leading-tight">
        Before you start
      </h2>
      <p className="text-[13px] text-massing-ink-mid leading-snug max-w-3xl mb-5">
        The complete checklist of items a NSW residential project should verify
        before lodging a DA or CDC. Items the pipeline could measure or fetch
        server-side are <strong>verified</strong>; everything else is flagged
        <strong> manual</strong> with a one-line pointer to where you should
        verify it. Nothing is silently assumed.
      </p>

      {/* Summary chips — counts per status. Click a chip to filter the
          list to just those items so the planner can drill into "what's
          still outstanding" or "what's blocking". */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <ChecklistFilterChip
          label="All"
          count={checklist.total}
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          styleKey="all"
        />
        <ChecklistFilterChip
          label="Verified"
          count={summary.verified}
          active={filter === 'verified'}
          onClick={() => setFilter('verified')}
          styleKey="verified"
        />
        <ChecklistFilterChip
          label="Needs manual check"
          count={summary.manual}
          active={filter === 'manual'}
          onClick={() => setFilter('manual')}
          styleKey="manual"
        />
        {summary.blocking > 0 && (
          <ChecklistFilterChip
            label="Blocking"
            count={summary.blocking}
            active={filter === 'blocking'}
            onClick={() => setFilter('blocking')}
            styleKey="blocking"
          />
        )}
        <ChecklistFilterChip
          label="Not applicable"
          count={summary.not_applicable}
          active={filter === 'not_applicable'}
          onClick={() => setFilter('not_applicable')}
          styleKey="not_applicable"
        />
      </div>

      <div className="space-y-6">
        {filteredSections.map((section) => (
          <ChecklistSectionBlock key={section.id} section={section} />
        ))}
        {filteredSections.length === 0 && (
          <div className="text-[13px] text-massing-ink-light italic">
            No items match this filter.
          </div>
        )}
      </div>
    </section>
  )
}

function ChecklistFilterChip({
  label,
  count,
  active,
  onClick,
  styleKey,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
  styleKey: 'all' | ChecklistStatus
}) {
  const pillCls = styleKey === 'all'
    ? 'bg-massing-card text-massing-ink border-massing-rule'
    : CHECKLIST_STATUS_STYLE[styleKey].pillCls
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 px-2.5 py-1 text-[11.5px] font-medium rounded-full border transition-opacity ' +
        pillCls +
        (active ? ' ring-1 ring-massing-ink/30' : ' opacity-70 hover:opacity-100')
      }
    >
      <span className="font-semibold">{count}</span>
      <span>{label}</span>
    </button>
  )
}

function ChecklistSectionBlock({ section }: { section: ChecklistSectionT }) {
  return (
    <div>
      <h3 className="font-serif text-lg text-massing-ink leading-tight mb-1">
        {section.title}
      </h3>
      <p className="text-[12px] text-massing-ink-light leading-snug mb-2.5 max-w-3xl">
        {section.description}
      </p>
      <ul className="space-y-1.5">
        {section.items.map((item) => (
          <ChecklistItemRow key={item.id} item={item} />
        ))}
      </ul>
    </div>
  )
}

function ChecklistItemRow({ item }: { item: ChecklistItemT }) {
  const style = CHECKLIST_STATUS_STYLE[item.status]
  return (
    <li
      className={
        'flex items-start gap-3 rounded-sm border border-massing-rule border-l-[3px] px-3 py-2.5 ' +
        style.border + ' ' + style.bg
      }
    >
      <span
        className={
          'flex-shrink-0 w-5 text-center text-base font-bold leading-snug mt-0.5 ' +
          style.markerCls
        }
        aria-hidden
      >
        {style.marker}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[13.5px] font-medium text-massing-ink leading-snug">
            {item.label}
          </span>
          <span
            className={
              'flex-shrink-0 text-[10px] tracking-wide uppercase px-1.5 py-[1px] rounded border ' +
              style.pillCls
            }
          >
            {style.pill}
          </span>
        </div>
        {item.detail && (
          <div className="text-[12.5px] text-massing-ink-mid leading-snug mt-1">
            {item.detail}
          </div>
        )}
        {item.hint && (
          <div className="text-[12px] text-massing-ink-light italic leading-snug mt-1">
            {item.hint}
          </div>
        )}
        {item.source && (
          <div className="brand-eyebrow text-massing-ink-light mt-1.5 text-[9.5px]">
            Source: {item.source}
          </div>
        )}
      </div>
    </li>
  )
}

// --------------------------------------------------------------------
// "Prepared by" — end-of-report consultant card. Renders the same
// snapshot the PDF embeds (report.prepared_by), so historical
// reports keep their original credit even when the admin edits
// the singleton profile later. Photo URL is the public endpoint
// rather than the data URL (which is in the payload too but bloats
// the JSON unnecessarily on the wire).
// --------------------------------------------------------------------

interface PreparedByT {
  full_name: string | null
  title: string | null
  registration_no: string | null
  email: string | null
  phone: string | null
  website: string | null
  has_photo: boolean
  photo_mime: string | null
  photo_data_url: string | null
}

function PreparedByCard({ preparedBy }: { preparedBy: PreparedByT }) {
  const photoSrc = preparedBy.photo_data_url ?? (preparedBy.has_photo ? consultantPhotoUrl() : null)
  return (
    <section className="mt-10 pt-6 border-t border-massing-rule-soft">
      <div className="brand-eyebrow text-massing-ink-light mb-3">Prepared by</div>
      <div className="flex items-center gap-5 p-5 rounded-lg bg-massing-cream/50 border border-massing-rule">
        {photoSrc && (
          <img
            src={photoSrc}
            alt=""
            className="w-20 h-20 rounded-full object-cover border border-massing-rule shrink-0"
          />
        )}
        <div className="min-w-0">
          {preparedBy.full_name && (
            <div className="font-serif text-xl text-massing-ink leading-tight">
              {preparedBy.full_name}
            </div>
          )}
          {preparedBy.title && (
            <div className="text-[13px] text-massing-ink-mid mt-0.5">
              {preparedBy.title}
            </div>
          )}
          {preparedBy.registration_no && (
            <div className="brand-eyebrow text-massing-ink-light mt-1.5">
              Registration · {preparedBy.registration_no}
            </div>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[12px] text-massing-ink-mid">
            {preparedBy.email && (
              <a href={`mailto:${preparedBy.email}`} className="hover:text-massing-ink">
                {preparedBy.email}
              </a>
            )}
            {preparedBy.phone && <span>{preparedBy.phone}</span>}
            {preparedBy.website && (
              <a
                href={preparedBy.website.startsWith('http') ? preparedBy.website : `https://${preparedBy.website}`}
                target="_blank"
                rel="noreferrer"
                className="hover:text-massing-ink"
              >
                {preparedBy.website}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

// Single end-of-report reliability disclaimer covering how parsed
// values are derived and what a planner should still do before relying
// on the numbers for a DA / CDC application. Avoids per-row "AI" /
// "engine" noise — the same disclosure once, where the planner can
// actually read it.
function MethodNote() {
  return (
    <section className="mt-12 pt-6 border-t border-massing-rule-soft">
      <div className="brand-eyebrow text-massing-ink-light mb-2">Method & limitations</div>
      <div className="text-[12.5px] text-massing-ink-mid leading-relaxed max-w-3xl space-y-2">
        <p>
          Spatial overlays (zoning, FSR map, height map, bushfire, flood,
          heritage, biodiversity) are pulled directly from the relevant
          NSW spatial portals and treated as authoritative — these rows
          carry a <span className="text-flag-green font-medium">green</span> confidence flag.
        </p>
        <p>
          Clause-level values from the LEP, DCP, and any uploaded Section
          10.7 certificate or Contract of Sale are <em>read</em> from the
          source document by the platform's review engine and may carry
          a <span className="text-flag-amber font-medium">medium</span> confidence flag. Every such value
          shows the source clause, verbatim text excerpt, and last-review
          date in its Source panel — verify the source clause before
          relying on the value for a DA / CDC application.
        </p>
        <p>
          This report is a feasibility indication, not a planning
          determination. Survey, structural, civil, and environmental
          assessments are still required.
        </p>
      </div>
    </section>
  )
}

type VerdictReasonT = {
  kind: 'block' | 'caveat' | 'confirm'
  text: string
  citation: string | null
  source: string | null
}

type NextStepT = {
  kind: 'block' | 'caveat'
  title: string
  body: string
  cost: string | null
  time: string | null
  reason_text: string
}

type CDCVerdictT = {
  status: 'yes' | 'conditional' | 'no'
  headline: string
  summary: string
  pathway: string
  housing_form: string
  reasons: VerdictReasonT[]
  cert_attached: boolean
  next_steps: NextStepT[]
}

// Surfaces when a previously-uploaded Section 10.7 cert or Contract
// of Sale exists for this lot but wasn't applied to this run (because
// the user didn't pick a file in this session). Click Apply and the
// report re-runs with apply_uploaded_docs=true so the cached findings
// fold into Section B / H / I.
function CachedDocumentsBanner({
  cached,
  onApply,
}: {
  cached: { cert: any | null; contract: any | null } | null | undefined
  onApply: () => void
}) {
  if (!cached) return null
  if (!cached.cert && !cached.contract) return null
  const fmtDate = (s: string | null | undefined) => {
    if (!s) return null
    try {
      const d = new Date(s)
      if (Number.isNaN(d.getTime())) return s
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return s
    }
  }
  const bits: string[] = []
  if (cached.cert) {
    const parts: string[] = ['Section 10.7 certificate']
    if (cached.cert.council_name) parts.push(`from ${cached.cert.council_name}`)
    if (cached.cert.cert_number) parts.push(`#${cached.cert.cert_number}`)
    const when = fmtDate(cached.cert.issued_at) ?? fmtDate(cached.cert.parsed_at)
    if (when) parts.push(`(${when})`)
    bits.push(parts.join(' '))
  }
  if (cached.contract) {
    const parts: string[] = ['Contract of sale']
    if (cached.contract.vendor) parts.push(`from ${cached.contract.vendor}`)
    const when = fmtDate(cached.contract.contract_date) ?? fmtDate(cached.contract.parsed_at)
    if (when) parts.push(`(${when})`)
    bits.push(parts.join(' '))
  }
  return (
    <div className="rounded-md border border-massing-highlight/40 glass px-4 py-3 flex flex-wrap items-start justify-between gap-3">
      <div className="text-sm text-massing-ink min-w-0">
        <div className="brand-eyebrow text-massing-ink-light mb-1">Previously uploaded for this lot</div>
        <div className="leading-snug">
          {bits.join(' · ')}
        </div>
        <div className="text-[12px] text-massing-ink-light italic mt-1.5">
          Not applied to this run. Click Apply to fold the findings into Section B and the CDC verdict.
        </div>
      </div>
      <button
        type="button"
        onClick={onApply}
        className="shrink-0 bg-massing hover:bg-massing-deep text-massing-cream text-sm px-3.5 py-1.5 rounded-sm transition-colors"
      >
        Apply findings
      </button>
    </div>
  )
}

// Hook: polls /feasibility/{id}/analyst every 2 s while the backend
// reports status='running', so the analyst section fills in
// progressively and the parent can gate other affordances (PDF
// download) on the same live state. Seeded from the initial
// response so a terminal status doesn't trigger a pointless first
// poll. Elapsed-second counter ticks alongside so the UI can show
// the planner the section is alive.
function useAnalystStatus(
  reportId: string,
  initial: {
    analyst: any | null | undefined
    status?: string | null
    failureReason?: string | null
  },
) {
  const [analyst, setAnalyst] = useState<any | null | undefined>(initial.analyst)
  const [status, setStatus] = useState<string | null | undefined>(initial.status)
  const [failureReason, setFailureReason] = useState<string | null | undefined>(initial.failureReason)
  const [elapsedSec, setElapsedSec] = useState<number>(0)
  const startedAt = useRef<number>(Date.now())

  // New report mounted (different id) → reset local state and the
  // wall-clock counter so the polling loop tracks the right row.
  useEffect(() => {
    setAnalyst(initial.analyst)
    setStatus(initial.status)
    setFailureReason(initial.failureReason)
    startedAt.current = Date.now()
    setElapsedSec(0)
  }, [reportId, initial.analyst, initial.status, initial.failureReason])

  useEffect(() => {
    if (status !== 'running') return
    let cancelled = false
    const tick = async () => {
      try {
        const res = await getAnalystStatus(reportId)
        if (cancelled) return
        setAnalyst(res.analyst)
        setStatus(res.analyst_status)
        setFailureReason(res.analyst_failure_reason)
      } catch {
        // Network blip — swallow and let the next interval retry.
      }
    }
    const interval = window.setInterval(tick, 2000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [reportId, status])

  useEffect(() => {
    if (status !== 'running') return
    const t = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAt.current) / 1000))
    }, 1000)
    return () => window.clearInterval(t)
  }, [status])

  return { analyst, status, failureReason, elapsedSec }
}

// Cosmetic "what the analyst is thinking about" rotation. The
// backend doesn't actually emit per-stage progress — the Sonnet
// call is opaque — so this is essentially a brand-aligned ticker
// that hints at what the layer reconciles. The labels match the
// canonical TOPIC_KEYS the analyst prompt walks through, so the
// rotation tracks reality even though the timing is local.
const ANALYST_TASKS: string[] = [
  'Cross-checking land-use permissibility',
  'Reading bushfire-prone overlays + BAL implications',
  'Reviewing contaminated-land findings',
  'Reconciling flood planning controls',
  'Checking heritage and conservation overlays',
  'Scanning biodiversity values and BAM triggers',
  'Reading coastal-management SEPP zones',
  'Looking at acid-sulfate soils',
  'Reading easements, covenants, and 88B encumbrances',
  'Considering subdivision pathways',
  'Checking acoustic and road-noise implications',
  'Reading traffic and access constraints',
  'Looking at ANEF aircraft-noise contours',
  'Checking mine-subsidence districts',
  'Reading paper-subdivision constraints',
  'Looking at drinking-water catchment overlays',
  'Reading salinity findings',
  'Stitching everything into a per-topic reconciliation',
]

// Roughly 3.5 s per task — gives the planner enough time to read
// each line and matches the typical 30–90 s analyst runtime across
// 17 tasks. After the last task we hold on "stitching…" until the
// real result lands.
const TASK_INTERVAL_S = 3.5

function FindingsAnalysisRunning({ elapsedSec }: { elapsedSec: number }) {
  // Active task index — clamped to the last entry once we've cycled
  // through them all so the panel doesn't loop back to the start
  // when the model takes longer than the cosmetic timeline.
  const rawIdx = Math.floor(elapsedSec / TASK_INTERVAL_S)
  const idx = Math.min(rawIdx, ANALYST_TASKS.length - 1)
  const currentTask = ANALYST_TASKS[idx]
  // Last 3 completed tasks — fade out below the active row so the
  // panel reads as a ticking trail, not a static skeleton.
  const recent = ANALYST_TASKS.slice(Math.max(0, idx - 3), idx).reverse()
  // Progress bar fills proportional to position through the task
  // list. Caps at ~94 % while running so it never reads "done"
  // before the real result arrives.
  const progressPct = Math.min(94, ((idx + 1) / ANALYST_TASKS.length) * 100)

  return (
    <section className="rounded-xl border border-massing-rule glass overflow-hidden">
      <div className="px-6 py-5 border-b border-massing-rule-soft">
        <div className="flex items-center gap-3 mb-3">
          <div className="brand-eyebrow text-massing-ink-light">Findings analysis</div>
          <span className="text-[11px] text-massing-ink-light font-mono ml-auto">
            {elapsedSec}s
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Gradient orb — palette-aware, gently pulses to signal
              the analyst is actively thinking. Bigger and brighter
              than a plain bullet so the eye lands here first. */}
          <span className="relative inline-flex w-3.5 h-3.5 items-center justify-center shrink-0">
            <span className="absolute inset-0 rounded-full brand-gradient-bg opacity-70 animate-ping" />
            <span className="relative w-2.5 h-2.5 rounded-full brand-gradient-bg" />
          </span>
          <span className="font-serif text-xl brand-gradient-text leading-snug">
            Findings analyst is thinking…
          </span>
        </div>
        <p className="text-[13px] text-massing-ink-mid leading-snug mt-2">
          Reconciling Section B findings, contract encumbrances, and
          spatial overlays into a per-topic read for your housing
          form. Usually 30–90 seconds — the rest of the report below
          is final.
        </p>
      </div>

      {/* Live ticker — current task in gradient text, recent tasks
          fading below. Key on idx so React swaps the node and the
          fade-in transition restarts each cycle. */}
      <div className="px-6 py-5 space-y-3">
        <div className="flex items-start gap-3" key={`cur-${idx}`}>
          <span
            className="inline-block w-2 h-2 rounded-full brand-gradient-bg mt-2 shrink-0 animate-pulse"
            aria-hidden
          />
          <span className="text-[15px] font-medium brand-gradient-text leading-snug">
            {currentTask}…
          </span>
        </div>
        <div className="space-y-1.5 pl-5">
          {recent.map((task, i) => (
            <div
              key={`r-${idx}-${i}`}
              className="text-[12px] text-massing-ink-light leading-snug flex items-center gap-2"
              style={{ opacity: 0.85 - i * 0.22 }}
            >
              <span className="text-massing-ink-light/70">✓</span>
              <span>{task}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Brand-gradient progress bar across the footer — subtle
          forward motion without claiming a precise percentage. */}
      <div className="h-[3px] bg-massing-rule-soft relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 brand-gradient-bg transition-all duration-700 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </section>
  )
}

// Findings analysis — the analyst layer's per-topic reconciliation.
// Renders just under the cached-docs banner, ABOVE the CDC verdict,
// so the planner gets the "what does this lot mean for my project"
// read before the raw verdict. Each topic is a compact card with
// status pill + plain-English implication + required actions + an
// expandable evidence list (with verbatim ClauseSnippets) so the
// planner can verify the analyst's reading.
//
// --------------------------------------------------------------------
// Executive summary + "read this first" disclaimer. The client-facing
// page: what's achievable, what isn't, what to do first — written by
// the analyst in plain English with each point referencing the
// section that carries the detail.
//
// The disclaimer box ALWAYS renders — it's the load-bearing part and
// must be visible even when the analyst hasn't produced a summary
// (still running, failed, or an older cached row from before the
// field existed). The summary content renders when present.
// --------------------------------------------------------------------

// Outcomes at a glance — council DA vs CDC for each form assessed.
// Deterministic (server-built from Sections C / D / E / J), so it
// renders whether or not the analyst wrote a summary.
function OutcomesAtAGlance({ outcomes }: { outcomes: Outcomes | null | undefined }) {
  if (!outcomes || !Array.isArray(outcomes.forms) || outcomes.forms.length === 0) return null
  const kindCls = (k: string) =>
    k === 'yes' ? 'text-flag-green' : k === 'conditional' ? 'text-flag-amber' : k === 'no' ? 'text-flag-red' : 'text-massing'
  const gfa = (v: number | null) => (v == null ? '—' : `${Math.round(v).toLocaleString()} m²`)
  const torrens = (t: boolean | null) => (t === true ? 'Available' : t === false ? 'Not available' : 'Verify')
  const cell = 'py-1.5 pr-3 align-top'
  const Row = ({ label, a, b }: { label: string; a: React.ReactNode; b: React.ReactNode }) => (
    <tr className="border-t border-massing-rule-soft">
      <th className="py-1.5 pr-3 text-left brand-eyebrow align-top w-[28%]">{label}</th>
      <td className={cell}>{a ?? '—'}</td>
      <td className={cell}>{b ?? '—'}</td>
    </tr>
  )
  const gfaCell = (p: FormOutcome['council']) => (
    <span>
      <span className="text-xl font-semibold text-massing-ink">{gfa(p.max_gfa_m2)}</span>
      {p.basis && <span className="block text-[11px] text-massing-ink-light leading-snug">{p.basis}</span>}
    </span>
  )
  return (
    <div className="mt-5">
      <div className="brand-eyebrow text-massing mb-2">Outcomes at a glance</div>
      {outcomes.combined && outcomes.headline && (
        <p className="text-[13.5px] text-massing-ink leading-snug max-w-3xl mb-3">
          {outcomes.headline}
          {outcomes.recommended_form_label && (
            <>
              {' '}Best outlook: <strong>{outcomes.recommended_form_label}</strong>.
            </>
          )}
        </p>
      )}
      <div className="space-y-4">
        {outcomes.forms.map((f) => (
          <div key={f.housing_form} className="rounded-md border border-massing-rule surface-shade px-4 py-3 overflow-x-auto">
            {outcomes.combined && <FormBlockHead label={f.label} sublabel={f.sublabel} />}
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th></Th>
                  <Th>Council DA</Th>
                  <Th>CDC (complying development)</Th>
                </tr>
              </thead>
              <tbody>
                <Row
                  label="Pathway open?"
                  a={<span className={`font-semibold ${kindCls(f.council.kind)}`}>{f.council.status}</span>}
                  b={<span className={`font-semibold ${kindCls(f.cdc.kind)}`}>{f.cdc.status}</span>}
                />
                <Row label="Maximum floor area" a={gfaCell(f.council)} b={gfaCell(f.cdc)} />
                <Row label="Approval time" a={f.council.approval_time} b={f.cdc.approval_time} />
                <Row label="Approval cost" a={f.council.approval_cost} b={f.cdc.approval_cost} />
                <Row label="Design flexibility" a={f.council.design_flexibility} b={f.cdc.design_flexibility} />
                <Row label="Risk" a={f.council.risk} b={f.cdc.risk} />
                {f.dual_occupancy && (
                  <tr className="border-t border-massing-rule-soft">
                    <th className="py-1.5 pr-3 text-left brand-eyebrow align-top">Torrens subdivision into two lots</th>
                    <td className={cell} colSpan={2}>
                      {torrens(f.dual_occupancy.torrens_subdivision)} — see Section J
                      {f.dual_occupancy.binding_pathway_label && (
                        <span className="block text-[11px] text-massing-ink-light leading-snug">
                          Governing pathway: {f.dual_occupancy.binding_pathway_label}
                        </span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="mt-2.5 text-[13px] text-massing-ink leading-snug">
              <strong>Rules-based recommendation:</strong> {f.recommended ?? '—'}
              {f.rationale && <> — {f.rationale}</>}
              {f.difference_text && <> {f.difference_text}</>}
            </p>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11px] italic text-massing-ink-light max-w-3xl">
        Figures come from Sections C, D, E and J. The recommendation is quantitative only — the final pathway should reflect design intent, timing and risk tolerance.
      </p>
    </div>
  )
}

function ExecutiveSummaryCard({
  summary,
  status,
  disclaimer,
  outcomes,
}: {
  summary: ExecutiveSummary | null
  status: string | null
  disclaimer: string | null
  outcomes?: Outcomes | null
}) {
  const sectionLabel = (ref: string | null) =>
    ref ? <span className="brand-eyebrow text-massing-ink-light ml-2 whitespace-nowrap">→ Section {ref}</span> : null

  return (
    <section className="rounded-xl border border-massing-rule glass overflow-hidden">
      <div className="px-6 py-5">
        <div className="brand-eyebrow text-massing-ink-light mb-2">Executive summary</div>

        {summary ? (
          <>
            <p className="font-serif text-[19px] text-massing-ink leading-snug max-w-3xl">
              {summary.bottom_line}
            </p>

            {summary.recommended_path && (
              <div className="mt-4 inline-flex items-baseline gap-3 px-3 py-1.5 rounded-md border border-massing-rule surface-shade">
                <span className="brand-eyebrow text-massing-ink-light">Recommended path</span>
                <span className="text-[14px] font-semibold text-massing-ink">{summary.recommended_path}</span>
              </div>
            )}

            <OutcomesAtAGlance outcomes={outcomes} />

            {(summary.achievable.length > 0 || summary.not_achievable.length > 0) && (
              <div className="mt-5 grid md:grid-cols-2 gap-3">
                {summary.achievable.length > 0 && (
                  <div className="rounded-md border border-flag-green/40 bg-flag-green/5 border-l-[3px] border-l-flag-green px-4 py-3">
                    <div className="brand-eyebrow text-flag-green mb-2">What you can do</div>
                    <ul className="space-y-2">
                      {summary.achievable.map((p, i) => (
                        <li key={i} className="text-[13.5px] text-massing-ink leading-snug">
                          {p.text}{sectionLabel(p.section_ref)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {summary.not_achievable.length > 0 && (
                  <div className="rounded-md border border-flag-red/40 bg-flag-red/5 border-l-[3px] border-l-flag-red px-4 py-3">
                    <div className="brand-eyebrow text-flag-red mb-2">What you can't (or shouldn't count on)</div>
                    <ul className="space-y-2">
                      {summary.not_achievable.map((p, i) => (
                        <li key={i} className="text-[13.5px] text-massing-ink leading-snug">
                          {p.text}{sectionLabel(p.section_ref)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {summary.first_actions.length > 0 && (
              <div className="mt-4 rounded-md border border-massing-rule surface-shade px-4 py-3">
                <div className="brand-eyebrow text-massing-ink-light mb-2">Do these first</div>
                <ol className="list-decimal pl-5 space-y-1.5 text-[13.5px] text-massing-ink leading-snug">
                  {summary.first_actions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ol>
              </div>
            )}
          </>
        ) : status === 'running' ? (
          <>
            <p className="text-[13px] text-massing-ink-mid italic flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full brand-gradient-bg animate-pulse" aria-hidden />
              Writing your summary — it lands once the findings analysis finishes.
            </p>
            <OutcomesAtAGlance outcomes={outcomes} />
          </>
        ) : (
          <>
            <p className="text-[13px] text-massing-ink-mid italic">
              The written summary wasn't generated for this run — the detailed sections below are complete and unaffected.
            </p>
            <OutcomesAtAGlance outcomes={outcomes} />
          </>
        )}
      </div>

      {disclaimer && (
        <div className="px-6 py-4 border-t-2 border-flag-amber bg-flag-amber/10">
          <div className="brand-eyebrow text-flag-amber font-semibold mb-1.5">Read this first</div>
          <p className="text-[13px] text-massing-ink leading-relaxed max-w-3xl">{disclaimer}</p>
        </div>
      )}
    </section>
  )
}

// Pure presentational — live status / polling live in
// `useAnalystStatus` on the parent so the PDF-download gate and
// this section share one source of truth.
function FindingsAnalysis({
  analyst,
  status,
  failureReason,
  elapsedSec,
}: {
  analyst: any | null | undefined
  status?: string | null
  failureReason?: string | null
  elapsedSec?: number
}) {

  // Empty / non-success states all surface a visible placeholder so
  // the planner can see at a glance which analyst path they're on
  // (running, failed, unconfigured, or ran-but-empty). Previously
  // any non-'failed' state returned null and the section vanished.
  if (!analyst || !Array.isArray(analyst.topics) || analyst.topics.length === 0) {
    if (status === 'running') {
      return <FindingsAnalysisRunning elapsedSec={elapsedSec ?? 0} />
    }
    const message =
      status === 'failed'
        ? "Analysis temporarily unavailable — the review engine couldn't reconcile findings on this run. The raw Section B controls and supporting documents below are unaffected. Try again in a minute, or re-run the feasibility."
        : status === 'unavailable'
        ? 'Analysis was not run for this report — the review engine is not configured in this environment. The raw Section B controls below are unaffected.'
        : analyst && Array.isArray(analyst.topics) && analyst.topics.length === 0
        ? 'Analysis ran but returned no topics for this lot — the model produced no reconciled findings. Re-run the feasibility to retry.'
        : 'Analysis not attempted for this run — re-run the feasibility to generate the per-topic reconciliation.'
    return (
      <section className="rounded-xl border border-flag-amber/35 surface-shade-md px-6 py-4">
        <div className="brand-eyebrow text-massing-ink-light mb-1">Findings analysis</div>
        <p className="text-[13px] text-massing-ink-mid leading-snug">{message}</p>
        {failureReason && (
          <p className="text-[11px] text-massing-ink-light font-mono mt-2 leading-snug">
            reason: {failureReason}
          </p>
        )}
        {!failureReason && status && (
          <p className="text-[11px] text-massing-ink-light font-mono mt-2 leading-snug">
            status: {status}
          </p>
        )}
      </section>
    )
  }
  const topics = analyst.topics as Array<any>
  return (
    <section className="rounded-xl border border-massing-rule glass overflow-hidden">
      <div className="px-6 py-5 border-b border-massing-rule-soft">
        <div className="brand-eyebrow text-massing-ink-light mb-1.5">Findings analysis</div>
        {analyst.headline && (
          <div className="font-serif text-xl text-massing-ink leading-snug">
            {analyst.headline}
          </div>
        )}
        {(analyst.biggest_risk || (analyst.missing_information ?? []).length > 0) && (
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            {analyst.biggest_risk && (
              <div className="surface-shade rounded-md px-3 py-2.5 border border-massing-rule-soft">
                <div className="brand-eyebrow text-massing-ink-light mb-1">Biggest risk</div>
                <div className="text-[13px] text-massing-ink leading-snug">{analyst.biggest_risk}</div>
              </div>
            )}
            {(analyst.missing_information ?? []).length > 0 && (
              <div className="surface-shade rounded-md px-3 py-2.5 border border-massing-rule-soft">
                <div className="brand-eyebrow text-massing-ink-light mb-1">What would resolve open questions</div>
                <ul className="text-[13px] text-massing-ink-mid leading-snug list-disc pl-5 space-y-0.5">
                  {analyst.missing_information.map((m: string, i: number) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      <ul className="divide-y divide-massing-rule-soft">
        {topics.map((t, i) => (
          <FindingsTopicRow key={i} topic={t} />
        ))}
      </ul>
    </section>
  )
}

function FindingsTopicRow({ topic }: { topic: any }) {
  const [open, setOpen] = useState(false)
  const tone = (topic.status_tone ?? 'info') as string
  const toneClasses =
    tone === 'ok'      ? 'bg-flag-green border-flag-green/45' :
    tone === 'caveat'  ? 'bg-flag-amber border-flag-amber/45' :
    tone === 'blocker' ? 'bg-flag-red border-flag-red/45' :
    tone === 'info'    ? 'border-massing-rule surface-shade-md' :
                         'border-massing-rule surface-shade'
  const toneSwatch =
    tone === 'ok'      ? 'bg-flag-green' :
    tone === 'caveat'  ? 'bg-flag-amber' :
    tone === 'blocker' ? 'bg-flag-red' :
                         'bg-massing-ink-light/40'
  const evidence = (topic.evidence ?? []) as Array<any>
  const actions = (topic.actions ?? []) as string[]
  return (
    <li className="px-6 py-4">
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${toneSwatch}`} aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
            <span className="font-serif text-[16px] text-massing-ink">{topic.label}</span>
            <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border ${toneClasses} ${tone === 'ok' || tone === 'caveat' || tone === 'blocker' ? 'on-flag' : 'text-massing-ink-mid'}`}>
              {topic.status}
            </span>
            <span className="text-[10.5px] uppercase tracking-wide text-massing-ink-light">
              {topic.confidence ?? '—'} confidence
            </span>
          </div>
          {topic.implication && (
            <p className="text-[13.5px] text-massing-ink-mid leading-relaxed mt-2 max-w-3xl">
              {topic.implication}
            </p>
          )}
          {topic.metrics && Object.keys(topic.metrics).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {Object.entries(topic.metrics as Record<string, string | number | boolean | null>).map(([k, v]) => (
                <span
                  key={k}
                  className="text-[10.5px] inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded border border-massing-rule surface-shade text-massing-ink-mid"
                  title={`${k}: ${formatMetricValue(v)}`}
                >
                  <span className="brand-eyebrow text-massing-ink-light">{formatMetricKey(k)}</span>
                  <span className="text-massing-ink font-medium">{formatMetricValue(v)}</span>
                </span>
              ))}
            </div>
          )}
          {actions.length > 0 && (
            <div className="mt-3">
              <div className="brand-eyebrow text-massing-ink-light mb-1">Actions</div>
              <ul className="text-[13px] text-massing-ink leading-snug list-disc pl-5 space-y-0.5">
                {actions.map((a, i) => (<li key={i}>{a}</li>))}
              </ul>
            </div>
          )}
          {evidence.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="text-[11px] uppercase tracking-wide text-massing hover:underline"
              >
                {open ? 'Hide evidence' : `Show evidence (${evidence.length})`}
              </button>
              {open && (
                <div className="mt-2 space-y-3">
                  {evidence.map((e, i) => (
                    <div key={i}>
                      <div className="brand-eyebrow text-massing-ink-light mb-1">{e.label}</div>
                      {e.source_text ? (
                        <ClauseSnippet
                          text={e.source_text}
                          citation={e.citation ?? null}
                          truncate={500}
                        />
                      ) : (
                        <div className="text-[12px] text-massing-ink-light italic">
                          {e.citation || 'No verbatim text recorded.'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {topic.confidence_reason && (
                <div className="text-[11px] text-massing-ink-light italic mt-2 leading-snug">
                  {topic.confidence_reason}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  )
}

// Small chip-row formatters for the analyst topic's metrics field.
// Keys arrive in snake_case from the model — humanise for the eye.
function formatMetricKey(k: string): string {
  return k.replace(/_/g, ' ').replace(/(^|\s)\S/g, (m) => m.toUpperCase())
}

function formatMetricValue(v: string | number | boolean | null): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'number') {
    // Three-decimal cap so 0.55 stays 0.55 but 12.345678 becomes 12.346
    return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/\.?0+$/, '')
  }
  return v
}

// ---------------------------------------------------------------
// Combined single-dwelling + duplex run (sections.L_form_comparison)
// ---------------------------------------------------------------
// The report assesses BOTH forms on the lot. The hero compares them
// side by side — the deterministic outlook plus the handful of
// numbers that differ — then each form gets its own full verdict
// card. Sections C / D / E render one framed sub-block per form under
// a single section heading so the client reads the two options like
// for like; A / B / F–K are form-independent and stay single.

function FormComparisonHero({ comparison }: { comparison: FormComparison }) {
  const forms = comparison.forms
  const permittedText = (p: boolean | null) =>
    p === true ? 'Yes' : p === false ? 'No' : 'Verify'
  return (
    <section className="space-y-6">
      <div className="rounded-xl border border-massing-rule glass px-6 py-6">
        <div className="brand-eyebrow text-massing-ink-light mb-2">Two options assessed on this lot</div>
        <h2 className="font-serif text-2xl md:text-[28px] leading-tight text-massing-ink">
          {comparison.headline}
        </h2>
        {comparison.recommended_label && (
          <p className="mt-2 text-sm text-massing-ink-mid max-w-3xl leading-relaxed">
            Best outlook: <strong className="text-massing-ink">{comparison.recommended_label}</strong>.
            Every section that depends on the housing form (C, D, E, J) carries a block for
            each, so the two can be compared like for like.
          </p>
        )}
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th></Th>
                {forms.map((b) => (
                  <Th key={b.housing_form}>{b.label}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              <CompareRowN
                label="Outlook"
                cells={forms.map((b) => (
                  <OutlookChip key={b.housing_form} outlook={b.headline.outlook} phrase={b.headline.outlook_phrase} />
                ))}
              />
              <CompareRowN label="Assessed as" cells={forms.map((b) => b.sublabel)} />
              <CompareRowN label="Permitted in zone" cells={forms.map((b) => permittedText(b.headline.permitted))} />
              <CompareRowN label="CDC eligibility" cells={forms.map((b) => (b.headline.cdc_status ?? '—').toUpperCase())} />
              <CompareRowN label="Council DA max GFA" cells={forms.map((b) => fmt(b.headline.council_max_gfa_m2, 'm²'))} />
              <CompareRowN label="CDC max GFA" cells={forms.map((b) => fmt(b.headline.cdc_max_gfa_m2, 'm²'))} />
              <CompareRowN label="Recommended pathway" cells={forms.map((b) => b.headline.recommended_pathway ?? '—')} />
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {forms.map((b) => (
          <div key={b.housing_form}>
            <FormBlockHead label={b.label} sublabel={b.sublabel} />
            <CDCVerdictCard verdict={b.cdc_verdict} />
          </div>
        ))}
      </div>
    </section>
  )
}

function CompareRowN({ label, cells }: { label: string; cells: React.ReactNode[] }) {
  return (
    <tr className="border-t border-massing-rule-soft">
      <td className="py-2 pr-3 text-massing-ink-light w-1/3">{label}</td>
      {cells.map((c, i) => (
        <td key={i} className="py-2 pr-3">{c}</td>
      ))}
    </tr>
  )
}

function OutlookChip({ outlook, phrase }: { outlook: FormOutlook; phrase: string }) {
  const cls =
    outlook === 'go' ? 'text-flag-green' :
    outlook === 'caution' ? 'text-flag-amber' :
    outlook === 'da_only' ? 'text-massing' :
    'text-flag-red'
  return <span className={`font-medium ${cls}`}>{phrase}</span>
}

function FormBlockHead({ label, sublabel }: { label: string; sublabel: string }) {
  return (
    <div className="flex items-baseline gap-2.5 mb-4 pb-2 border-b border-massing-rule">
      <span className="font-serif text-xl text-massing-ink">{label}</span>
      <span className="text-[11px] uppercase tracking-[0.18em] text-massing-ink-light">{sublabel}</span>
    </div>
  )
}

/** One framed per-form block inside a section (C / D / E), or a
 *  bare labelled wrapper when `framed` is false (next steps). */
function FormSubBlock({
  id,
  label,
  sublabel,
  framed = true,
  children,
}: {
  id?: string
  label: string
  sublabel: string
  framed?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      id={id}
      className={
        framed
          ? 'scroll-mt-24 mt-6 first:mt-0 rounded-lg border border-massing-rule-soft px-5 py-5'
          : 'scroll-mt-24'
      }
    >
      <FormBlockHead label={label} sublabel={sublabel} />
      {children}
    </div>
  )
}

// ---------------------------------------------------------------
// Sections C / D / E bodies — factored out so the combined run can
// render one per form. The single-form path calls each once with
// the flat report.sections values.
// ---------------------------------------------------------------

function CouncilPathwayBody({ C, A, lga }: { C: any; A: any; lga: string | null }) {
  return (
    <>
      <PermittedUseCard
        form={C.housing_form as string | undefined}
        result={C.permitted_use as PermittedUseT | undefined}
      />
      <YieldHero
        siteAreaM2={A.geometry.area_m2}
        maxGfaM2={C.max_gfa_m2}
        bindingConstraint={C.binding_constraint}
        pathways={C.yield_pathways ?? []}
        diagnostics={(C.diagnostics ?? []) as Diagnostic[]}
      />
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <SubHeader>Resolved LEP controls</SubHeader>
          <SourceMeta
            name={C.resolved_lep.lep_name}
            version={C.resolved_lep.version_date}
            parserModel={C.resolved_lep.parser_model}
            parsedAt={C.resolved_lep.parsed_at}
          />
          <table className="w-full text-sm">
            <tbody>
              <KV label="FSR" valueNode={<ValueCell v={C.resolved_lep.fsr as ValueT} label="FSR (resolved)" overrideContext={lga ? { target_type: 'lep', lga_slug: lga, zone: (A.zone as ValueT).value as string } : undefined} fieldKey="fsr" />} />
              <KV label="Height" valueNode={<ValueCell v={C.resolved_lep.height_m as ValueT} unit="m" label="Height (resolved)" overrideContext={lga ? { target_type: 'lep', lga_slug: lga, zone: (A.zone as ValueT).value as string } : undefined} fieldKey="height" />} />
            </tbody>
          </table>
        </div>
        <div>
          <SubHeader>DCP controls applied</SubHeader>
          <SourceMeta
            name={C.resolved_dcp.dcp_name}
            version={C.resolved_dcp.version_date}
          />
          <DcpControlsTable
            controls={C.resolved_dcp.controls as Record<string, ValueT>}
            dcpName={C.resolved_dcp.dcp_name}
            extras={C.resolved_dcp.extras}
            lga={lga}
            zone={(A.zone as ValueT).value as string}
          />
        </div>
      </div>

      {Array.isArray(C.resolved_dcp.extras) && C.resolved_dcp.extras.length > 0 && (
        <div className="mt-6">
          <SubHeader>Council-specific controls</SubHeader>
          <p className="text-xs text-massing-ink-light mb-2 max-w-3xl">
            Controls captured from this DCP that don't fit the standard
            fields above — typically character / heritage-area rules,
            residential flat building floor-plate ratios, excavation
            limits, etc. Verify each against the source chapter before
            relying.
          </p>
          <table className="w-full text-sm">
            <tbody>
              {C.resolved_dcp.extras.map((e: any, i: number) => (
                <tr key={i} className="border-t border-massing-rule-soft">
                  <td className="py-2 pr-3 w-1/3">{e.label}</td>
                  <td className="py-2">
                    <div>
                      {e.value == null ? '—' : `${e.value}${e.unit ? ' ' + e.unit : ''}`}
                    </div>
                    {e.clause && (
                      <div className="text-[11px] text-massing-ink-light mt-0.5 flex items-center gap-1.5">
                        {e.clause}
                        <CopyExtraClauseButton text={e.clause} />
                      </div>
                    )}
                    {e.source_text && (
                      <details className="mt-2 text-[11px] text-massing-ink-light">
                        <summary className="cursor-pointer text-massing hover:underline">Show source</summary>
                        <div className="mt-2">
                          <ClauseSnippet text={e.source_text} citation={e.clause ?? null} />
                        </div>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {C.envelope && (
        <div className="mt-6">
          <SubHeader>Envelope</SubHeader>
          <table className="w-full text-sm">
            <thead>
              <tr><Th>Storey</Th><Th>Footprint</Th><Th>Binding constraint</Th></tr>
            </thead>
            <tbody>
              {C.envelope.footprints.map((f: any) => (
                <tr key={f.storey} className="border-t border-massing-rule-soft">
                  <td className="py-1.5 pr-3">{f.storey}</td>
                  <td className="py-1.5 pr-3">{fmt(f.area_m2, 'm²')}</td>
                  <td className="py-1.5">{f.binding_constraint}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SummaryCard
        rows={[
          ['FSR GFA cap', fmt(C.fsr_gfa_m2, 'm²')],
          ['Envelope GFA', fmt(C.envelope?.gfa_m2, 'm²')],
        ]}
        headline={['Council Max GFA', fmt(C.max_gfa_m2, 'm²')]}
        footnote={`Binding constraint: ${C.binding_constraint || '—'}`}
      />
    </>
  )
}

/** Which clause the CDC GFA cap comes from, by housing form — mirrors
 *  `gfa_cap()` on the server so the label can't drift from the number. */
function gfaCapLabel(form?: string | null): string {
  if (!form) return 'CDC GFA cap'
  if (form.startsWith('dual_occupancy')) return 'Codes SEPP cl 3B.10 GFA cap'
  if (form === 'manor_house') return 'Codes SEPP cl 3B.23 GFA cap'
  if (form === 'terrace' || form === 'multi_dwelling_housing') return 'Codes SEPP cl 3B.35 GFA cap'
  if (form === 'residential_flat_building') return 'CDC GFA cap (no CDC pathway)'
  return 'Housing Code cl 3.9 GFA cap'
}

function CdcPathwayBody({ D, cdcBlocked }: { D: any; cdcBlocked: boolean }) {
  // When CDC is blocked (the headline verdict already says so),
  // there's no point shoving the planner through pages of irrelevant
  // Housing Code numbers — collapse the body behind a disclosure so
  // it's still recoverable for the curious but not a time-waster.
  const inner = (
    <>
      <div className="text-xs text-massing-ink-light italic mb-4 max-w-3xl">{D.disclaimer}</div>
      <div className="mb-3">
        <span className="brand-eyebrow mr-2">Eligibility</span>
        <span className="font-serif text-lg">{D.eligibility.status.toUpperCase()}</span>
        <span className="text-massing-ink-light text-sm ml-2">· {D.eligibility.housing_form}</span>
      </div>
      {D.eligibility.disqualifiers.length > 0 && (
        <div className="mb-4">
          <SubHeader>Disqualifiers</SubHeader>
          <ul className="list-disc pl-5 text-sm space-y-1">
            {D.eligibility.disqualifiers.map((d: any, i: number) => (
              <li key={i}><span className="font-medium">{d.reason}</span> <span className="text-massing-ink-light">— {d.citation}</span></li>
            ))}
          </ul>
        </div>
      )}
      {D.eligibility.criteria_met.length > 0 && (
        <div className="mb-4">
          <SubHeader>Criteria met</SubHeader>
          <ul className="list-disc pl-5 text-sm text-massing-ink-mid space-y-1">
            {D.eligibility.criteria_met.map((c: string, i: number) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(D.applied_controls) && D.applied_controls.length > 0 && (
        <div className="mb-4">
          <SubHeader>SEPP-applied controls</SubHeader>
          <table className="w-full text-sm">
            <thead><tr><Th>Control</Th><Th>Value</Th><Th>Citation</Th></tr></thead>
            <tbody>
              {D.applied_controls.map((c: any, i: number) => (
                <tr key={i} className="border-t border-massing-rule-soft">
                  <td className="py-1.5 pr-3">{c.label}</td>
                  <td className="py-1.5 pr-3">
                    {c.value == null ? '—' : `${c.value}${c.unit ? ' ' + c.unit : ''}`}
                  </td>
                  <td className="py-1.5 text-massing-ink-light">{c.citation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <SummaryCard
        rows={[
          [gfaCapLabel(D.eligibility.housing_form), fmt(D.gfa_cap_m2, 'm²')],
          ['CDC envelope GFA', fmt(D.envelope_gfa_m2, 'm²')],
        ]}
        headline={['CDC Max GFA', fmt(D.max_cdc_gfa_m2, 'm²')]}
      />
      <GfaBreakdownPanel breakdown={D.gfa_breakdown} />
    </>
  )
  if (cdcBlocked) {
    return (
      <details className="rounded-md border border-massing-rule-soft px-4 py-3 text-sm">
        <summary className="cursor-pointer text-massing-ink-mid italic select-none">
          CDC isn't an option for this form — Council DA is the only pathway.
          <span className="text-massing-ink-light ml-2">Show the Housing Code detail anyway →</span>
        </summary>
        <div className="mt-4">
          {inner}
        </div>
      </details>
    )
  }
  return inner
}

function PathwayComparisonBody({ E }: { E: any }) {
  return (
    <>
      <table className="w-full text-sm">
        <thead>
          <tr><Th></Th><Th>Council DA</Th><Th>CDC (Codes SEPP)</Th></tr>
        </thead>
        <tbody>
          <CompareRow label="Max GFA" a={fmt(E.council.max_gfa_m2, 'm²')} b={fmt(E.cdc.max_gfa_m2, 'm²')} />
          <CompareRow label="Binding constraint" a={E.council.binding_constraint} b={E.cdc.binding_constraint} />
          <CompareRow label="Approval time" a={E.council.approval_time} b={E.cdc.approval_time} />
          <CompareRow label="Approval cost" a={E.council.approval_cost} b={E.cdc.approval_cost} />
          <CompareRow label="Design flexibility" a={E.council.design_flexibility} b={E.cdc.design_flexibility} />
          <CompareRow label="Risk" a={E.council.risk} b={E.cdc.risk} />
        </tbody>
      </table>
      <div className="mt-6 p-6 rounded-lg glass brand-gradient-ring">
        <div className="brand-eyebrow text-massing-ink-light mb-1.5">Recommended</div>
        <div className="font-serif text-3xl brand-gradient-text tracking-tight">{E.recommended}</div>
        <div className="text-sm text-massing-ink-mid mt-2.5 max-w-3xl leading-relaxed">{E.rationale}</div>
      </div>
      <div className="text-xs text-massing-ink-light mt-3 italic max-w-3xl">{E.caveat}</div>
    </>
  )
}

function CDCVerdictCard({ verdict }: { verdict: CDCVerdictT | undefined }) {
  if (!verdict) return null
  // Status colour ramp — green / amber / red for yes / conditional / no.
  // Plus a giant headline keyed off the status so a planner can read
  // "CDC AVAILABLE" from across the room without scanning the prose.
  const isYes = verdict.status === 'yes'
  const isNo = verdict.status === 'no'
  const isConditional = verdict.status === 'conditional'
  const ring =
    isYes ? 'border-flag-green/50' :
    isConditional ? 'border-flag-amber/45' :
    'border-flag-red/55'
  const accent =
    isYes ? 'bg-flag-green' :
    isConditional ? 'bg-flag-amber' :
    'bg-flag-red'
  const accentText =
    isYes ? 'text-flag-green' :
    isConditional ? 'text-flag-amber' :
    'text-flag-red'
  // Headline + subline live on the server (build_cdc_verdict). The
  // server already tunes wording by status AND caveat count so a
  // single source of truth keeps the web view and PDF in sync.
  const giantHeadline = verdict.headline
  const subline = verdict.summary
  // Reasons grouped by kind so the planner sees blocks (red), caveats
  // (amber) and confirms (green) in priority order rather than the
  // server-side insertion order.
  const blocks = verdict.reasons.filter((r) => r.kind === 'block')
  const caveats = verdict.reasons.filter((r) => r.kind === 'caveat')
  const confirms = verdict.reasons.filter((r) => r.kind === 'confirm')

  return (
    <section className={`rounded-xl border ${ring} glass overflow-hidden`}>
      <div className="relative px-6 py-7">
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background:
              'radial-gradient(ellipse at 0% 100%, rgba(124,109,255,0.30), transparent 55%), radial-gradient(ellipse at 100% 0%, rgba(255,106,213,0.18), transparent 55%)',
          }}
        />
        <div className="relative">
          <div className="flex items-baseline gap-3 flex-wrap mb-3">
            <span className="brand-eyebrow text-massing-ink-light">CDC eligibility · via {verdict.pathway} · {verdict.housing_form}</span>
          </div>
          {/* Massive status headline — the visual hierarchy the
              planner anchors on. Coloured by status, with a thick
              accent bar on the left so it reads as "the answer". */}
          <div className="flex items-stretch gap-4 mb-4">
            <span className={`w-1.5 rounded-full ${accent}`} aria-hidden />
            <div>
              <h2 className={`brand-wordmark text-3xl md:text-4xl leading-[1.02] ${accentText}`}>
                {giantHeadline}
              </h2>
              <p className="mt-2 text-[14px] text-massing-ink leading-snug max-w-3xl">
                {subline}
              </p>
            </div>
          </div>
          {!verdict.cert_attached && (
            <div className="mt-4 text-[12px] text-massing-ink-mid border-l-2 border-flag-amber pl-3 max-w-3xl leading-relaxed">
              <span className="font-medium text-massing-ink">Documents needed.</span>{' '}
              A Section 10.7 certificate (and ideally the contract of sale + 88B) is required to confidently confirm CDC eligibility. Upload them on the form above to lock the answer in.
            </div>
          )}
        </div>
      </div>
      {verdict.reasons.length > 0 && (
        <div className="surface-shade-strong">
          {blocks.length > 0 && (
            <ReasonGroup label={isNo ? 'Why CDC is blocked' : 'Blockers'} items={blocks} />
          )}
          {caveats.length > 0 && (
            <ReasonGroup label="Caveats to verify" items={caveats} />
          )}
          {confirms.length > 0 && (
            <ReasonGroup label="Council confirmed" items={confirms} />
          )}
        </div>
      )}
    </section>
  )
}

function NextStepsCard({ verdict }: { verdict: CDCVerdictT | undefined }) {
  if (!verdict) return null
  // Next-step recommendations are computed server-side
  // (housing_code._next_steps_from_reasons) so the web view and PDF
  // share one source of truth — change wording in one place and both
  // surfaces stay in sync. Patterns covered: bushfire, flood,
  // heritage, contamination, land reservation, 88B covenants,
  // biodiversity, loose-fill asbestos.
  const items = verdict.next_steps ?? []

  if (items.length > 0) {
    // Itemised "things to resolve" — the planner's punch list.
    return (
      <section className="rounded-xl border border-massing-rule glass overflow-hidden">
        <div className="px-6 py-5 border-b border-massing-rule-soft">
          <div className="brand-eyebrow text-massing-ink-light mb-1">What to do next</div>
          <h3 className="font-serif text-2xl text-massing-ink leading-tight">
            {items.length === 1 ? 'One thing to resolve' : `${items.length} things to resolve`}
          </h3>
          <p className="text-sm text-massing-ink-mid mt-1.5 max-w-3xl leading-relaxed">
            Each item below maps a finding on this lot to the next concrete action — who to
            engage, what it typically costs, and how long it takes.
          </p>
        </div>
        <ItemsList items={items} />
      </section>
    )
  }

  // No itemised actions — the verdict has either nothing to do, or its
  // blockers / caveats don't match any of our advice patterns. We
  // ALWAYS render a section here so the card never silently
  // disappears between a first feasibility (spatial-only) and the
  // post-cert re-fetch — the box's continuity is part of the user's
  // trust in the report.
  if (verdict.status === 'yes') {
    return (
      <section className="rounded-xl border border-flag-green/35 glass overflow-hidden">
        <div className="px-6 py-5">
          <div className="brand-eyebrow text-massing-ink-light mb-1">What to do next</div>
          <h3 className="font-serif text-2xl text-massing-ink mb-1.5">You're clear to proceed</h3>
          <p className="text-sm text-massing-ink-mid max-w-3xl leading-relaxed">
            Council has confirmed every overlay in your favour and no encumbrance flagged
            binding constraints. Engage a designer / certifier to lodge the CDC application
            against the controls in Section D.
          </p>
        </div>
      </section>
    )
  }
  if (verdict.status === 'no') {
    return (
      <section className="rounded-xl border border-flag-red/35 glass overflow-hidden">
        <div className="px-6 py-5">
          <div className="brand-eyebrow text-massing-ink-light mb-1">What to do next</div>
          <h3 className="font-serif text-2xl text-massing-ink mb-1.5">Switch to a Council DA</h3>
          <p className="text-sm text-massing-ink-mid max-w-3xl leading-relaxed">
            The blocker(s) listed on the verdict above rule out the CDC pathway for this lot.
            Engage a town planner to scope a Development Application against the LEP / DCP
            controls in Section C — start with a pre-DA enquiry with Council to confirm the
            lodgement requirements.
          </p>
        </div>
      </section>
    )
  }
  // status === 'conditional' with no pattern-matched caveats — rare,
  // usually a covenant or precinct rule we haven't catalogued an
  // action for yet. Point the planner at the verdict above so the
  // card still gives them direction.
  return (
    <section className="rounded-xl border border-flag-amber/35 glass overflow-hidden">
      <div className="px-6 py-5">
        <div className="brand-eyebrow text-massing-ink-light mb-1">What to do next</div>
        <h3 className="font-serif text-2xl text-massing-ink mb-1.5">Resolve the caveats above</h3>
        <p className="text-sm text-massing-ink-mid max-w-3xl leading-relaxed">
          Every caveat on the verdict above has to clear before CDC is the safe pathway.
          Work through them with the relevant consultant — bushfire / flood / heritage /
          covenant specialists as applicable — and re-run the feasibility once the answers
          land.
        </p>
      </div>
    </section>
  )
}

function ItemsList({ items }: { items: NextStepT[] }) {
  return (
    <ol className="divide-y divide-massing-rule-soft">
      {items.map((it, i) => (
        <li key={i} className="px-6 py-4 flex items-start gap-4">
          <span className="brand-eyebrow text-massing-ink-light mt-0.5 tabular-nums">
            {String(i + 1).padStart(2, '0')}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap mb-1">
              <h4 className="font-medium text-[15px] text-massing-ink leading-snug">
                {it.title}
              </h4>
              <span className={
                'text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-semibold on-flag ' +
                (it.kind === 'block' ? 'bg-flag-red' : 'bg-flag-amber')
              }>
                {it.kind === 'block' ? 'Blocker' : 'Caveat'}
              </span>
            </div>
            <div className="text-[12.5px] text-massing-ink-light italic mb-1.5 leading-snug">
              Triggered by: {it.reason_text}
            </div>
            <p className="text-[13.5px] text-massing-ink-mid leading-relaxed">
              {it.body}
            </p>
            {(it.cost || it.time) && (
              <div className="mt-2 flex flex-wrap gap-4 text-[11.5px] text-massing-ink-light">
                {it.cost && (
                  <span><span className="brand-eyebrow mr-1">Cost</span>{it.cost}</span>
                )}
                {it.time && (
                  <span><span className="brand-eyebrow mr-1">Time</span>{it.time}</span>
                )}
              </div>
            )}
          </div>
        </li>
      ))}
    </ol>
  )
}

function ReasonGroup({ label, items }: { label: string; items: CDCVerdictT['reasons'] }) {
  return (
    <div className="border-t border-massing-rule-soft">
      <div className="px-6 pt-3 brand-eyebrow text-massing-ink-light">{label}</div>
      <ul className="divide-y divide-massing-rule-soft/60">
        {items.map((r, i) => (
          <li key={i} className="px-6 py-3 flex items-start gap-3">
            <ReasonDot kind={r.kind} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-massing-ink leading-snug">{r.text}</div>
              {(r.citation || r.source) && (
                <div className="text-[11px] text-massing-ink-light font-mono mt-0.5">
                  {[r.source, r.citation].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ReasonDot({ kind }: { kind: 'block' | 'caveat' | 'confirm' }) {
  const c =
    kind === 'block' ? 'bg-flag-red' :
    kind === 'caveat' ? 'bg-flag-amber' :
    'bg-flag-green'
  const sym = kind === 'block' ? '✕' : kind === 'caveat' ? '!' : '✓'
  return (
    <span className={`mt-0.5 w-5 h-5 rounded-full ${c} on-flag text-[11px] font-bold flex items-center justify-center shrink-0`}>{sym}</span>
  )
}

// ---- Dual occupancy assessment section -----------------------------------
//
// Renders only when the housing form is dual_occupancy_*. Cross-layer
// LEP / DCP / SEPP feasibility with conflict detection and a Torrens-
// vs-Strata subdivision read. The data is structured so the section
// stays honest: a "permitted_overall = false" routes to a loud red
// callout regardless of what the spatial-only CDC verdict said.

type DualOccupancyLayer = {
  layer: string
  permitted: boolean | null
  min_lot_m2: number | null
  min_frontage_m: number | null
  notes: string[]
  citation: string | null
  source_text?: string | null
}

type DualOccupancyAssessmentT = {
  housing_form: string
  permitted_overall: boolean | null
  binding_min_lot_m2: number | null
  binding_min_frontage_m: number | null
  lot_meets_min: boolean | null
  frontage_meets_min: boolean | null
  torrens_subdivision_allowed: boolean | null
  subdivision_notes: string[]
  layers: DualOccupancyLayer[]
  conflicts: string[]
  recommendations: string[]
}

type DualOccupancyComparisonT = {
  attached: DualOccupancyAssessmentT
  detached: DualOccupancyAssessmentT
  recommended_form: 'dual_occupancy_attached' | 'dual_occupancy_detached'
  summary: string
}

function DualOccupancySection({
  comparison,
  single,
}: {
  comparison: DualOccupancyComparisonT | null | undefined
  single: DualOccupancyAssessmentT | null | undefined
}) {
  // When the comparison is present, render the combined comparison.
  // Otherwise fall back to the single-card behaviour (an explicit
  // attached / detached pick, or a non-dual-occ form where neither
  // payload is set and both render null).
  if (comparison) {
    return <DualOccupancyComparisonCard comparison={comparison} />
  }
  return <DualOccupancyAssessmentCard assessment={single} />
}

function DualOccupancyComparisonCard({
  comparison,
}: {
  comparison: DualOccupancyComparisonT
}) {
  const { attached, detached, recommended_form, summary } = comparison
  const att_ok = attached.permitted_overall === true
  const det_ok = detached.permitted_overall === true
  // null = a state pathway is still open pending a check the tool
  // can't make (LMR-area status, Pattern Book site requirements).
  const att_pending = attached.permitted_overall === null
  const det_pending = detached.permitted_overall === null
  // Status colour of the OUTER ring + headline tracks the best
  // path — if either sub-form works, the section reads green; if
  // one still needs verification, amber; otherwise red.
  const anyOk = att_ok || det_ok
  const anyPending = att_pending || det_pending
  const ring = anyOk
    ? 'border-flag-green/45'
    : anyPending
    ? 'border-flag-amber/45'
    : 'border-flag-red/50'
  const headline = att_ok && det_ok
    ? 'Both attached and detached are feasible'
    : att_ok
    ? (det_pending ? 'Attached is feasible; detached needs verification' : 'Attached is feasible; detached is not')
    : det_ok
    ? (att_pending ? 'Detached is feasible; attached needs verification' : 'Detached is feasible; attached is not')
    : att_pending && det_pending
    ? 'Both attached and detached need verification'
    : att_pending
    ? 'Attached needs verification; detached is not feasible'
    : det_pending
    ? 'Detached needs verification; attached is not feasible'
    : 'Neither attached nor detached is feasible'
  return (
    <section className={`rounded-xl border ${ring} glass overflow-hidden`}>
      <div className="px-6 py-5">
        <div className="brand-eyebrow text-massing-ink-light mb-1">
          Section J · Dual occupancy assessment
        </div>
        <h3 className="brand-wordmark text-2xl md:text-3xl text-massing-ink leading-tight mb-2">
          {headline}
        </h3>
        <p className="text-sm text-massing-ink-mid max-w-3xl leading-relaxed">
          {summary}
        </p>
      </div>

      {/* Side-by-side per-sub-form breakdown. On mobile they stack. */}
      <div className="grid md:grid-cols-2 border-t border-massing-rule-soft divide-y md:divide-y-0 md:divide-x divide-massing-rule-soft/60">
        <DualOccupancySubformPanel
          label="Attached"
          assessment={attached}
          isRecommended={recommended_form === 'dual_occupancy_attached'}
        />
        <DualOccupancySubformPanel
          label="Detached"
          assessment={detached}
          isRecommended={recommended_form === 'dual_occupancy_detached'}
        />
      </div>
    </section>
  )
}

function DualOccupancySubformPanel({
  label,
  assessment,
  isRecommended,
}: {
  label: string
  assessment: DualOccupancyAssessmentT
  isRecommended: boolean
}) {
  const ok = assessment.permitted_overall === true
  const headlineColor =
    ok ? 'text-flag-green' :
    assessment.permitted_overall === false ? 'text-flag-red' :
    'text-flag-amber'
  return (
    <div className="px-5 py-4">
      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
        <span className="brand-eyebrow text-massing-ink-light">{label}</span>
        {isRecommended && (
          <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-semibold bg-massing-highlight/30 text-massing-ink">
            Recommended
          </span>
        )}
      </div>
      <div className={`text-lg font-medium ${headlineColor} mb-2`}>
        {ok ? 'Feasible' :
         assessment.permitted_overall === false ? 'Not feasible' :
         'Needs verification'}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
        <ThresholdChip
          label="Min lot"
          value={assessment.binding_min_lot_m2}
          unit="m²"
          ok={assessment.lot_meets_min}
        />
        <ThresholdChip
          label="Min frontage"
          value={assessment.binding_min_frontage_m}
          unit="m"
          ok={assessment.frontage_meets_min}
        />
      </div>
      {assessment.conflicts.length > 0 && (
        <ul className="space-y-1 text-[12.5px] text-massing-ink-mid mb-3 list-disc pl-4">
          {assessment.conflicts.map((c, i) => (
            <li key={i} className="text-flag-red/90">{c}</li>
          ))}
        </ul>
      )}
      {assessment.recommendations.length > 0 && (
        <ul className="space-y-1 text-[12.5px] text-massing-ink-mid leading-snug list-disc pl-4">
          {assessment.recommendations.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function DualOccupancyAssessmentCard({
  assessment,
}: {
  assessment: DualOccupancyAssessmentT | null | undefined
}) {
  if (!assessment) return null
  const status: 'yes' | 'no' | 'unknown' =
    assessment.permitted_overall === true ? 'yes' :
    assessment.permitted_overall === false ? 'no' :
    'unknown'
  const ring =
    status === 'yes' ? 'border-flag-green/45' :
    status === 'no' ? 'border-flag-red/55' :
    'border-flag-amber/45'
  const headline =
    status === 'yes' ? 'Dual occupancy is permitted on this lot' :
    status === 'no' ? 'Dual occupancy is NOT available on this lot' :
    'Dual occupancy needs verification'
  const headlineColor =
    status === 'yes' ? 'text-flag-green' :
    status === 'no' ? 'text-flag-red' :
    'text-flag-amber'
  const formLabel = assessment.housing_form === 'dual_occupancy_attached'
    ? 'attached'
    : 'detached'
  return (
    <section className={`rounded-xl border ${ring} glass overflow-hidden`}>
      <div className="px-6 py-5">
        <div className="brand-eyebrow text-massing-ink-light mb-1">
          Section J · Dual occupancy assessment · {formLabel}
        </div>
        <h3 className={`brand-wordmark text-2xl md:text-3xl ${headlineColor} leading-tight`}>
          {headline}
        </h3>
        <p className="mt-2 text-sm text-massing-ink-mid max-w-3xl leading-relaxed">
          Dual occupancy depends on the LEP land-use table and cl 4.1B minimum
          lot, the DCP&apos;s form-specific controls, and the State pathways:
          Codes SEPP Part 3B (LRHD Code) and Part 3BA (Pattern Book Code) for
          complying development, Housing SEPP 2021 Chapter 6 (s 166 in R2;
          s 168 inside a low and mid rise housing area) for a Council DA.
          &quot;Verify&quot; marks a check the tool can&apos;t make yet.
        </p>

        {/* Binding numeric thresholds — the most-restrictive across all
            three layers. The lot / frontage status chips give an
            instant read on whether the site clears them. */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <ThresholdChip
            label="Binding min lot"
            value={assessment.binding_min_lot_m2}
            unit="m²"
            ok={assessment.lot_meets_min}
          />
          <ThresholdChip
            label="Binding min frontage"
            value={assessment.binding_min_frontage_m}
            unit="m"
            ok={assessment.frontage_meets_min}
          />
          <SubdivisionChip allowed={assessment.torrens_subdivision_allowed} />
        </div>

        {/* Cross-layer conflicts — the highest-signal items.
            Surfaced loudly in red because they routinely override the
            CDC verdict above (LEP-prohibits flows through). */}
        {assessment.conflicts.length > 0 && (
          <div className="mt-4 rounded-md border border-flag-red/50 bg-flag-red/8 px-4 py-3">
            <div className="brand-eyebrow text-flag-red mb-1.5">Cross-layer conflicts</div>
            <ul className="space-y-1.5 text-[13.5px] text-massing-ink leading-relaxed">
              {assessment.conflicts.map((c, i) => (
                <li key={i} className="flex gap-2"><span aria-hidden>✕</span><span>{c}</span></li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Per-layer findings — LEP / DCP / SEPP each get their own row */}
      <div className="surface-shade-strong border-t border-massing-rule-soft">
        <div className="brand-eyebrow text-massing-ink-light px-6 pt-3">Per-layer findings</div>
        <ul className="divide-y divide-massing-rule-soft/60">
          {assessment.layers.map((l, i) => (
            <li key={i} className="px-6 py-3">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="flex items-baseline gap-3">
                  <span className="font-medium text-massing-ink">{l.layer}</span>
                  <PermittedChip permitted={l.permitted} />
                </div>
                {l.citation && (
                  <span className="text-[11px] text-massing-ink-light font-mono">{l.citation}</span>
                )}
              </div>
              <div className="mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12.5px] text-massing-ink-mid">
                {l.min_lot_m2 != null && (
                  <span><span className="brand-eyebrow mr-1">Min lot</span>{l.min_lot_m2.toLocaleString()} m²</span>
                )}
                {l.min_frontage_m != null && (
                  <span><span className="brand-eyebrow mr-1">Min frontage</span>{l.min_frontage_m} m</span>
                )}
              </div>
              {l.notes.length > 0 && (
                <ul className="mt-1.5 text-[12.5px] text-massing-ink-mid italic leading-snug space-y-0.5">
                  {l.notes.map((n, j) => (<li key={j}>· {n}</li>))}
                </ul>
              )}
              {l.source_text && (
                <div className="mt-2">
                  <ClauseSnippet
                    text={l.source_text}
                    source={l.layer}
                    citation={l.citation ?? null}
                    truncate={400}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Subdivision read — Torrens vs strata */}
      {assessment.subdivision_notes.length > 0 && (
        <div className="border-t border-massing-rule-soft px-6 py-4 surface-shade-strong">
          <div className="brand-eyebrow text-massing-ink-light mb-1.5">Subdivision feasibility</div>
          <ul className="text-[13px] text-massing-ink-mid leading-relaxed space-y-0.5">
            {assessment.subdivision_notes.map((n, i) => (<li key={i}>· {n}</li>))}
          </ul>
        </div>
      )}

      {/* Recommendations — plain-English actions */}
      {assessment.recommendations.length > 0 && (
        <div className="border-t border-massing-rule-soft px-6 py-4">
          <div className="brand-eyebrow text-massing-ink-light mb-1.5">Recommendations</div>
          <ul className="text-[13px] text-massing-ink leading-relaxed space-y-1.5">
            {assessment.recommendations.map((r, i) => (
              <li key={i} className="flex gap-2">
                <span className="brand-eyebrow text-massing-ink-light tabular-nums mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

function ThresholdChip({ label, value, unit, ok }: { label: string; value: number | null; unit: string; ok: boolean | null }) {
  const tone =
    ok === true ? 'bg-flag-green/12 border-flag-green/45 text-massing-ink' :
    ok === false ? 'bg-flag-red/15 border-flag-red/50 text-massing-ink' :
    'surface-shade border-massing-rule-soft text-massing-ink-mid'
  return (
    <div className={`px-3 py-2 rounded-md border ${tone}`}>
      <div className="brand-eyebrow text-massing-ink-light mb-0.5">{label}</div>
      <div className="text-[14px] tabular-nums">
        {value != null ? `${value.toLocaleString()} ${unit}` : '—'}
        {ok != null && (
          <span className="ml-1.5 text-[11px] font-medium">{ok ? '✓ meets' : '✕ below'}</span>
        )}
      </div>
    </div>
  )
}

function SubdivisionChip({ allowed }: { allowed: boolean | null }) {
  const tone =
    allowed === true ? 'bg-flag-green/12 border-flag-green/45' :
    allowed === false ? 'bg-flag-amber/12 border-flag-amber/45' :
    'surface-shade border-massing-rule-soft'
  const label =
    allowed === true ? 'Torrens + Strata' :
    allowed === false ? 'Strata only' :
    'Verify'
  return (
    <div className={`px-3 py-2 rounded-md border ${tone}`}>
      <div className="brand-eyebrow text-massing-ink-light mb-0.5">Subdivision</div>
      <div className="text-[14px] text-massing-ink">{label}</div>
    </div>
  )
}

function PermittedChip({ permitted }: { permitted: boolean | null }) {
  if (permitted === true) {
    return <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-semibold bg-flag-green on-flag">Permitted</span>
  }
  if (permitted === false) {
    return <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-semibold bg-flag-red on-flag">Prohibited</span>
  }
  return <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-semibold bg-flag-amber on-flag">Verify</span>
}

function SourceMeta({
  name,
  version,
  parserModel: _parserModel,   // intentionally unused — never surface vendor
  parsedAt,
}: {
  name?: string | null
  version?: string | null
  parserModel?: string | null
  parsedAt?: string | null
}) {
  if (!name) return null
  // Surface a stale-data warning when the planning instrument was last
  // reviewed more than ~12 months ago. NSW LEPs amend on average every
  // few months; a year-old read is plausibly out of date and the
  // planner should know without having to dig for the parsed_at.
  const ageDays = staleDays(parsedAt)
  const isStale = ageDays != null && ageDays > 365
  return (
    <p className="text-[11px] text-massing-ink-mid mb-2">
      <span className="font-medium">{name}</span>
      {version && <span> ({version})</span>}
      {parsedAt && (
        <span className="text-massing-ink-light italic">
          {' — reviewed '}{parsedAt.slice(0, 10)}
        </span>
      )}
      {isStale && (
        <span
          className="ml-2 align-middle inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-flag-amber/20 text-flag-amber border border-flag-amber/40 px-1.5 py-0.5 rounded"
          title="The cached planning instrument is more than a year old. Amendments may have superseded these controls — verify against the current gazetted version."
        >
          may be stale
        </span>
      )}
    </p>
  )
}

function staleDays(iso: string | null | undefined): number | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24))
}

type GfaBreakdownT = {
  housing_form: string
  lot_area_m2: number | null
  summary: string
  citation: string | null
  matched_row: GfaBracketRowT | null
  rows: GfaBracketRowT[]
}

type GfaBracketRowT = {
  area_min: number
  area_max: number | null
  max_floor_area_m2: number | null
  max_site_coverage_pct: number | null
  max_storeys: number | null
  citation: string
  applies: boolean
  // Verbatim display text from the gazetted table for formula-driven
  // brackets ("25% of lot area + 150 m²") and fixed entries ("235 m²")
  // alike. Backend-supplied so the UI doesn't have to re-derive it.
  formula_text?: string | null
  // True for brackets with a ">" lower bound (most of cl 3.9's rows).
  // Drives the "> 250 m²" prefix in the lot-area column.
  exclusive_min?: boolean
}

function GfaBreakdownPanel({ breakdown }: { breakdown: GfaBreakdownT | null | undefined }) {
  if (!breakdown) return null
  // No Codes SEPP form carries a site-coverage control, so the column
  // only renders when a row actually has a figure.
  const hasCover = breakdown.rows.some((r) => r.max_site_coverage_pct != null)
  return (
    <div className="mt-5 rounded-md glass">
      <div className="px-4 py-3 border-b border-massing-rule-soft bg-massing-cream flex items-baseline justify-between gap-3">
        <div>
          <div className="brand-eyebrow text-massing">How the GFA cap was derived</div>
          <div className="text-[12px] text-massing-ink-mid mt-0.5">{breakdown.summary}</div>
        </div>
        {breakdown.citation && (
          <div className="text-[11px] text-massing-ink-light font-mono shrink-0">{breakdown.citation}</div>
        )}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-massing-rule-soft">
            <th className="text-left brand-eyebrow py-2 px-4">Lot area bracket</th>
            <th className="text-left brand-eyebrow py-2 px-4">Max GFA</th>
            {hasCover && <th className="text-left brand-eyebrow py-2 px-4">Site cover</th>}
            <th className="text-left brand-eyebrow py-2 px-4">Max storeys</th>
            <th className="text-left brand-eyebrow py-2 px-4">Clause</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.rows.map((r, i) => (
            <tr
              key={i}
              className={`border-t border-massing-rule-soft ${r.applies ? 'bg-massing-cream' : ''}`}
            >
              <td className="py-2 px-4">
                <span className={r.applies ? 'font-medium text-massing-ink' : 'text-massing-ink-mid'}>
                  {r.exclusive_min ? '>' : ''}{r.area_min.toLocaleString()}{r.area_max != null ? `–${r.area_max.toLocaleString()}` : '+'} m²
                </span>
                {r.applies && (
                  <span className="ml-2 text-[10px] uppercase tracking-wide brand-gradient-bg text-[#0a0c1a] px-1.5 py-0.5 rounded font-semibold">
                    your lot
                  </span>
                )}
              </td>
              <td className="py-2 px-4 font-mono">
                {/* On the matched row, prefer the computed numeric cap;
                    on non-matched rows, surface the gazetted display
                    text verbatim (e.g. "25% of lot area + 150 m²") so
                    the planner sees the actual table, not a misleading
                    fixed number computed against the wrong lot area. */}
                {r.applies && r.max_floor_area_m2
                  ? `${r.max_floor_area_m2.toLocaleString()} m²`
                  : r.formula_text
                  ? r.formula_text
                  : r.max_floor_area_m2 != null
                  ? `${r.max_floor_area_m2.toLocaleString()} m²`
                  : '—'}
              </td>
              {hasCover && (
                <td className="py-2 px-4 font-mono">
                  {r.max_site_coverage_pct != null ? `${r.max_site_coverage_pct}%` : '—'}
                </td>
              )}
              <td className="py-2 px-4 font-mono">{r.max_storeys ?? '—'}</td>
              <td className="py-2 px-4 text-[11px] text-massing-ink-light">{r.citation}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function EncumbranceCard({
  e,
}: {
  e: {
    type: string
    label: string
    instrument: string | null
    description: string | null
    burden: string | null
    benefit: string | null
    affected_area_m2: number | null
    fsr_limit: number | null
    height_limit_m: number | null
    max_dwellings: number | null
    source_text: string | null
  }
}) {
  const isCovenant = e.type === 'restrictive_covenant' || e.type === 'positive_covenant'
  const typeLabel = e.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return (
    <div className={`border-l-2 pl-3 ${isCovenant ? 'border-massing-maroon' : 'border-massing'}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-medium text-massing-ink">{e.label}</span>
        <span className="text-[10px] uppercase tracking-wide bg-massing-rule-soft text-massing-ink-mid px-1.5 py-0.5 rounded">{typeLabel}</span>
        {e.instrument && <span className="text-[11px] text-massing-ink-light">· {e.instrument}</span>}
      </div>
      {e.description && (
        <div className="text-[12px] text-massing-ink-mid mt-0.5 leading-snug">{e.description}</div>
      )}
      <div className="text-[11px] text-massing-ink-light mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
        {e.burden && <span>Burden: {e.burden}</span>}
        {e.benefit && <span>Benefit: {e.benefit}</span>}
        {e.affected_area_m2 != null && <span>Area: ~{Math.round(e.affected_area_m2)} m²</span>}
        {e.fsr_limit != null && <span>Caps FSR at <strong>{e.fsr_limit}:1</strong></span>}
        {e.height_limit_m != null && <span>Caps height at <strong>{e.height_limit_m} m</strong></span>}
        {e.max_dwellings != null && <span>Max dwellings: <strong>{e.max_dwellings}</strong></span>}
      </div>
      {e.source_text && (
        <div className="mt-2">
          <ClauseSnippet
            text={e.source_text}
            source={`Contract of sale${e.instrument ? ` — ${e.instrument}` : ''}`}
            citation={e.label ?? null}
            truncate={500}
          />
        </div>
      )}
    </div>
  )
}

function ClauseExtract({
  source,
  citation,
  text,
}: {
  source: string
  citation: string
  text: string
}) {
  // Print the clause locator (citation) as the primary chip — that's
  // what a planner cares about. The instrument name (source) sits on
  // the right and is only rendered when it's distinct, so DCP entries
  // without a per-control citation read as one clean "Ku-ring-gai DCP"
  // chip rather than the same string twice on both sides.
  const hasCitation = (citation ?? '').trim().length > 0
  const showSource = (source ?? '').trim().length > 0 && source !== citation
  return (
    <div className="border-l-2 border-massing pl-4">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <span className="brand-eyebrow text-massing">
          {hasCitation ? citation : (source || '—')}
        </span>
        {showSource && hasCitation && (
          <span className="text-[11px] text-massing-ink-light uppercase tracking-wide">{source}</span>
        )}
      </div>
      <blockquote className="font-serif text-sm italic text-massing-ink whitespace-pre-wrap leading-relaxed">
        {text}
      </blockquote>
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <div className="brand-eyebrow mb-1">Section</div>
      <h2 className="font-serif text-2xl text-massing border-b border-massing-rule pb-2">{children}</h2>
    </div>
  )
}

function SubHeader({ children }: { children: React.ReactNode }) {
  return <h3 className="brand-eyebrow mb-2">{children}</h3>
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="text-left brand-eyebrow pb-2 pr-3">{children}</th>
}

function KV({ label, value, valueNode }: { label: string; value?: any; valueNode?: React.ReactNode }) {
  return (
    <tr className="border-t border-massing-rule-soft">
      <th className="py-1.5 pr-3 text-left brand-eyebrow w-40 align-top">{label}</th>
      <td className="py-1.5">{valueNode ?? (value ?? '—')}</td>
    </tr>
  )
}

// The planner inputs the engine reasoned from — LMR-area status and
// the second public road — with their source, or a plain "not checked"
// / "none recorded" so a run made without them says so on page one.
function SiteContextRows({ sc }: { sc: any }) {
  if (!sc) return null
  const note = 'block text-[11px] text-massing-ink-light italic mt-0.5'
  const rows: React.ReactNode[] = []
  if (sc.lmr_relevant) {
    const status = sc.lmr_area === true ? 'Inside' : sc.lmr_area === false ? 'Outside' : 'Not checked'
    const source: string | null = sc.lmr_area_source ?? null
    rows.push(
      <KV
        key="lmr"
        label="Low and Mid-Rise Housing area"
        valueNode={
          <span>
            {status}
            <span className={note}>
              {sc.lmr_area == null
                ? "No status was entered and no map layer answered, so the report treats the LEP figures as binding. Check the Department's Low and Mid-Rise Housing map and enter the answer on the form: inside an area, Housing SEPP s 168 sets a 450 m² / 12 m duplex standard and s 169 allows 225 m² Torrens lots."
                : source
                  ? source.charAt(0).toUpperCase() + source.slice(1) + '.'
                  : null}
            </span>
          </span>
        }
      />,
    )
  }
  if (sc.second_road_access || sc.dual_occ_assessed) {
    rows.push(
      <KV
        key="road"
        label="Second road access"
        valueNode={
          sc.second_road_access ? (
            <span>
              {sc.second_road_access_label ?? sc.second_road_access}
              <span className={note}>
                {sc.second_road_access_source === 'planner'
                  ? 'Supplied by the planner from the deposited plan; not independently verified by this tool.'
                  : 'Detected from the cadastre polygon; verify on the deposited plan.'}
              </span>
            </span>
          ) : (
            <span>
              None recorded
              <span className={note}>
                The report assumes one street frontage. If the lot runs through to a second street or a rear lane, enter it on the form — it lowers the Codes SEPP width minimum for a duplex to 12 m (cl 3B.8(2)(a)) and allows a front-and-back Torrens split.
              </span>
            </span>
          )
        }
      />,
    )
  }
  return <>{rows}</>
}

// Section A property summary. Dimensions (site area, frontage, depth)
// can be edited inline; saving triggers a full re-run via onRerun and
// the parent App swaps in the loading state.
function PropertySummaryCard({
  A,
  onRerun,
}: {
  A: any
  onRerun?: (overrides: Partial<FeasibilityRequest>) => Promise<void> | void
}) {
  const geom = A.geometry ?? {}
  const [editing, setEditing] = useState(false)
  const round1 = (n: number | null | undefined) =>
    n != null ? String(Math.round(n * 10) / 10) : ''
  const [area, setArea] = useState<string>(round1(geom.area_m2))
  const [frontage, setFrontage] = useState<string>(round1(geom.primary_frontage_m))
  const [depth, setDepth] = useState<string>(round1(geom.estimated_depth_m))
  // Per-field "touched" tracking so we only send overrides for
  // dimensions the user actually changed in this edit pass. Without
  // this, opening the editor and clicking Save would tag every row as
  // user input even though the inputs were just pre-filled.
  const [touched, setTouched] = useState<{ area: boolean; frontage: boolean; depth: boolean }>({
    area: false,
    frontage: false,
    depth: false,
  })

  const areaUserInput = !!geom.area_user_supplied
  const frontageUserInput = !!geom.frontage_user_supplied
  const depthUserInput = !!geom.depth_user_supplied
  const anyOverridden = areaUserInput || frontageUserInput || depthUserInput

  function startEdit() {
    setArea(round1(geom.area_m2))
    setFrontage(round1(geom.primary_frontage_m))
    setDepth(round1(geom.estimated_depth_m))
    setTouched({ area: false, frontage: false, depth: false })
    setEditing(true)
  }

  async function save() {
    if (!onRerun) return
    const parseNum = (s: string): number | null => {
      const t = s.trim()
      if (!t) return null
      const n = Number(t)
      return Number.isFinite(n) && n > 0 ? n : null
    }
    // Only include fields the user actually edited. App.onRerun merges
    // these over the previous request, so untouched dimensions keep
    // whatever was previously sent (override OR estimate). A touched
    // field with a blank input becomes null, which clears the override
    // on the backend and reverts that row to the polygon estimate.
    const overrides: Partial<FeasibilityRequest> = {}
    if (touched.area) overrides.site_area_m2 = parseNum(area)
    if (touched.frontage) overrides.frontage_m = parseNum(frontage)
    if (touched.depth) overrides.depth_m = parseNum(depth)
    setEditing(false)
    if (Object.keys(overrides).length === 0) return
    await onRerun(overrides)
  }

  async function resetToPlatform() {
    if (!onRerun) return
    setEditing(false)
    await onRerun({ site_area_m2: null, frontage_m: null, depth_m: null })
  }

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <table className="w-full text-sm">
          <tbody>
            <KV label="LGA" value={A.lga_name} />
            <KV label="Applicable LEP" value={A.lep_name} />
            <KV label="Lot / DP" value={A.lot_dp} />
            {(() => {
              const titles: string[] = Array.isArray(A.titles) ? A.titles : []
              const problems: string[] = (Array.isArray(A.title_notes) ? A.title_notes : []).filter(
                (n: string) => /Not found|separate parcels/.test(n),
              )
              if (titles.length <= 1 && problems.length === 0) return null
              return (
                <KV
                  label="Titles"
                  valueNode={
                    <span>
                      {titles.join('; ')}
                      <span className="block text-[11px] text-massing-ink-light italic mt-0.5">
                        {titles.length > 1 && `${A.title_count} titles in one site — consolidate before subdividing. `}
                        {problems.join(' ')}
                      </span>
                    </span>
                  }
                />
              )
            })()}
            <SiteContextRows sc={A.site_context} />
            <DimensionRow
              label="Site area"
              value={fmt(geom.area_m2, 'm²')}
              userInput={areaUserInput}
              editing={editing}
              inputValue={area}
              onChange={(v) => { setArea(v); setTouched((t) => ({ ...t, area: true })) }}
              unit="m²"
            />
            <DimensionRow
              label="Frontage"
              value={fmt(geom.primary_frontage_m, 'm')}
              userInput={frontageUserInput}
              measured={!frontageUserInput && !!geom.frontage_confident}
              editing={editing}
              inputValue={frontage}
              onChange={(v) => { setFrontage(v); setTouched((t) => ({ ...t, frontage: true })) }}
              unit="m"
            />
            <DimensionRow
              label="Depth"
              value={fmt(geom.estimated_depth_m, 'm')}
              userInput={depthUserInput}
              editing={editing}
              inputValue={depth}
              onChange={(v) => { setDepth(v); setTouched((t) => ({ ...t, depth: true })) }}
              unit="m"
            />
            <KV label="Shape" value={geom.shape_descriptor} />
            <KV
              label="Zone"
              valueNode={
                <ValueCell
                  v={A.zone as ValueT}
                  label="Zone"
                />
              }
            />
          </tbody>
        </table>
        {onRerun && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {!editing ? (
              <>
                <button
                  type="button"
                  onClick={startEdit}
                  className="text-[11px] uppercase tracking-wide px-2.5 py-1 border border-massing-rule rounded-md text-massing-ink-light hover:text-massing-ink hover:border-massing-highlight transition-colors"
                >
                  Edit dimensions
                </button>
                {anyOverridden && (
                  <button
                    type="button"
                    onClick={resetToPlatform}
                    className="text-[11px] uppercase tracking-wide px-2.5 py-1 border border-massing-rule rounded-md text-massing-ink-light hover:text-massing-ink hover:border-massing-highlight transition-colors"
                    title="Discard your overrides and re-run with the platform's polygon-derived measurements."
                  >
                    Reset to platform values
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={save}
                  className="bg-massing hover:bg-massing-deep text-massing-cream text-[12px] uppercase tracking-wide px-3 py-1.5 rounded-sm transition-colors"
                >
                  Save & re-run
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="text-[11px] uppercase tracking-wide px-2.5 py-1 text-massing-ink-light hover:text-massing-ink"
                >
                  Cancel
                </button>
                <span className="text-[11px] text-massing-ink-light italic">
                  Re-runs the full feasibility with your values, updates the PDF.
                </span>
              </>
            )}
          </div>
        )}
      </div>
      <MapView
        latitude={A.latitude}
        longitude={A.longitude}
        polygon={A.lot_polygon as number[][] | undefined}
      />
    </div>
  )
}

function DimensionRow({
  label,
  value,
  userInput,
  measured,
  editing,
  inputValue,
  onChange,
  unit,
}: {
  label: string
  value: string
  userInput: boolean
  measured?: boolean
  editing: boolean
  inputValue: string
  onChange: (s: string) => void
  unit: string
}) {
  const badge = userInput ? 'User input' : measured ? 'Measured' : 'Estimate'
  const badgeCls = userInput
    ? 'bg-massing-highlight/20 text-massing-highlight border-massing-highlight/40'
    : measured
    ? 'bg-flag-green/15 text-flag-green border-flag-green/30'
    : 'bg-massing-cream text-massing-ink-light border-massing-rule'
  return (
    <tr className="border-t border-massing-rule-soft">
      <th className="py-1.5 pr-3 text-left brand-eyebrow w-40 align-top">{label}</th>
      <td className="py-1.5">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="decimal"
              value={inputValue}
              onChange={(e) => onChange(e.target.value)}
              className="w-28 border border-massing-rule bg-massing-paper-deep px-2 py-1 text-massing-ink focus:outline-none focus:border-massing-highlight text-sm"
            />
            <span className="text-[12px] text-massing-ink-light">{unit}</span>
            <span className="text-[10px] uppercase tracking-wide text-massing-ink-light">
              blank to revert to estimate
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span>{value}</span>
            <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${badgeCls}`}>
              {badge}
            </span>
          </div>
        )}
      </td>
    </tr>
  )
}

function CompareRow({ label, a, b }: { label: string; a: any; b: any }) {
  return (
    <tr className="border-t border-massing-rule-soft">
      <th className="py-1.5 pr-3 text-left brand-eyebrow">{label}</th>
      <td className="py-1.5 pr-3">{a ?? '—'}</td>
      <td className="py-1.5">{b ?? '—'}</td>
    </tr>
  )
}

function SummaryCard({
  rows,
  headline,
  footnote,
}: {
  rows: [string, string][]
  headline: [string, string]
  footnote?: string
}) {
  return (
    <div className="mt-5 bg-massing-cream border border-massing-rule p-5">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between text-sm py-0.5">
          <span className="text-massing-ink-mid">{k}</span>
          <span>{v}</span>
        </div>
      ))}
      <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-massing-rule">
        <span className="brand-eyebrow">{headline[0]}</span>
        <span className="font-serif text-2xl text-massing">{headline[1]}</span>
      </div>
      {footnote && <div className="text-xs text-massing-ink-light mt-2">{footnote}</div>}
    </div>
  )
}

function OverlayRow({
  controlKey,
  v,
  resolvedHint,
  certApplies = false,
  certPending = false,
}: {
  controlKey: string
  v: ValueOverlay
  resolvedHint?: { value: unknown; citation?: string } | null
  // True when a s10.7 certificate is attached to this lot. A 10.7 is
  // exhaustive — if it didn't enumerate an overlay, the lot isn't
  // affected by it. So when a cert is attached, a spatial-layer
  // "not affected" is treated as confidently confirmed (not "verify
  // with s10.7").
  certApplies?: boolean
  // True while the s10.7 review job is in flight — instead of nudging
  // the planner to "verify with a 10.7" (which they've already given
  // us), we show an explicit loading state so they understand the
  // value will replace this cell once the cert finishes reviewing.
  certPending?: boolean
}) {
  // Section B is intentionally a pure info table — no buttons. Section
  // C is where decisions are made and where show-source / copy live.
  const meta = OVERLAY_LABELS[controlKey] ?? {
    label: controlKey.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  }
  // Status semantics:
  //   - `value`        — a real overlay value (bushfire category,
  //                      flood class, etc.) drives the AFFECTS THIS
  //                      LOT highlight.
  //   - `not_affected` — overlay was queried and explicitly returned
  //                      "no". Renders as "Not affected" copy.
  //   - `unavailable`  — couldn't query the layer.
  // Booleans need special handling: a `false` Value means "no", which
  // should read as "Not affected" rather than the literal string
  // "false" (which then incorrectly triggers AFFECTS THIS LOT because
  // `false != null` and `false !== ''`).
  const status =
    v.status ??
    (v.value === false
      ? 'not_affected'
      : v.value != null
      ? 'value'
      : v.confidence === 'green'
      ? 'not_affected'
      : 'unavailable')
  const unit = meta.unit ?? ''
  let display: React.ReactNode
  if (status === 'value') {
    display = (
      <span>
        {renderValue(v.value)}{unit ? ` ${unit}` : ''}
        {resolvedHint && (
          <span className="text-massing-ink-light">
            {' → '}
            <span className="text-massing-ink font-medium">
              {renderValue(resolvedHint.value)}{unit ? ` ${unit}` : ''}
            </span>
            {resolvedHint.citation && (
              <span className="text-[11px]"> ({resolvedHint.citation.split(' sliding')[0]})</span>
            )}
            <span className="text-[11px]"> — see Section C</span>
          </span>
        )}
      </span>
    )
  } else if (status === 'not_affected') {
    // Spatial-layer-only "not affected" can be wrong on its own — the
    // NSW overlays occasionally lag the council register. There are
    // three confidence levels:
    //   1. The cert explicitly negated this overlay → "Not affected"
    //      (carries the `via s10.7` tag on row).
    //   2. A cert is attached but didn't enumerate this overlay → the
    //      10.7 is exhaustive by design, so silence = not affected.
    //      "Not affected" without the via-s10.7 chip.
    //   3. No cert attached → spatial layer alone, planner should
    //      upload a 10.7 to confirm. "Verify with s10.7 certificate".
    const certConfirmed = v.override_applied && (v.source ?? '').toLowerCase().includes('10.7')
    if (certConfirmed) {
      display = 'Not affected'
    } else if (certApplies) {
      display = 'Not affected'
    } else if (certPending) {
      // s10.7 is being reviewed right now — this cell will be
      // replaced with the council-issued answer the moment the review
      // finishes (the parent App silently re-fetches feasibility on
      // job completion).
      display = (
        <span className="inline-flex items-center gap-2 text-massing-ink-mid italic">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full brand-gradient-bg animate-pulse"
            aria-hidden
          />
          Reading your s10.7…
        </span>
      )
    } else {
      display = <span className="text-massing-ink-mid">Verify with s10.7 certificate</span>
    }
  } else if (status === 'not_on_map') {
    display = 'Not on map — see Section C'
  } else {
    display = 'Verify manually'
  }
  // "Affects this lot" means a real overlay value is present — bushfire
  // category, heritage item, flood class etc. These rows get the
  // loudest treatment: amber-tinted background, an accent bar on the
  // left, bold value text, and an AFFECTS THIS LOT chip beside it.
  // Everything else (not affected, pending, value-not-loaded) stays
  // subtle so the eye lands on what actually changes the answer.
  const isAffected =
    status === 'value' &&
    v.value != null &&
    v.value !== '' &&
    // Numeric controls like FSR / height are always "values" but the
    // affecting-overlay highlight is only for binary YES findings
    // (bushfire / heritage / flood / contamination / etc).
    !['fsr', 'height_of_buildings', 'min_lot_size_m2', 'zone'].includes(controlKey)

  return (
    <tr className={
      'border-t border-massing-rule-soft ' +
      (isAffected ? 'bg-flag-amber/10' : '')
    }>
      <td className={'py-2.5 pr-3 w-1/3 ' + (isAffected ? 'border-l-2 border-flag-amber pl-3' : '')}>
        <div className={isAffected ? 'text-massing-ink font-medium' : ''}>{meta.label}</div>
        {meta.help && (
          <div className="text-[11px] text-massing-ink-light mt-0.5">{meta.help}</div>
        )}
      </td>
      <td className="py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={isAffected ? 'text-massing-ink font-semibold' : ''}>{display}</span>
          {isAffected && (
            <span className="text-[10px] uppercase tracking-[0.12em] bg-flag-amber on-flag px-1.5 py-0.5 rounded font-semibold">
              Affects this lot
            </span>
          )}
          {v.override_applied && v.source && v.source.toLowerCase().includes('10.7') && (
            <span className="text-[10px] uppercase tracking-wide brand-gradient-bg text-[#0a0c1a] px-1.5 py-0.5 rounded font-semibold">
              via s10.7
            </span>
          )}
        </div>
        {v.override_applied && v.source && v.source.toLowerCase().includes('10.7') && v.source_text && (
          <div className="mt-2 max-w-2xl">
            <ClauseSnippet
              text={v.source_text}
              source="Section 10.7 certificate"
              citation={v.citation ?? null}
              truncate={500}
            />
          </div>
        )}
      </td>
    </tr>
  )
}

const OVERLAY_LABELS: Record<string, { label: string; help?: string; unit?: string }> = {
  zone: { label: 'Zone', help: 'LEP land-use zone applying to the lot.' },
  fsr: { label: 'FSR (map)', help: 'Floor space ratio from the LEP FSR Map — overlay clauses may reduce this.' },
  height_of_buildings: { label: 'Height of buildings (map)', help: 'Max height from the HoB Map — defers to LEP cl 4.3 if blank.', unit: 'm' },
  heritage: { label: 'Heritage', help: 'Listed heritage item or conservation area on the lot.' },
  land_reservation_acquisition: { label: 'Land reservation', help: 'Acquisition flag for public-purpose reservations.' },
  acid_sulfate_soils: { label: 'Acid sulfate soils', help: 'Soil class 1–5 (lower number = higher risk).' },
  riparian_land: { label: 'Riparian / watercourses', help: 'Land near a watercourse subject to stream-protection controls.' },
  biodiversity_values: { label: 'Biodiversity values', help: 'Mapped on the Biodiversity Values Map (BAM trigger).' },
  drinking_water_catchment: { label: 'Drinking water catchment', help: 'Sydney drinking-water catchment overlay.' },
  bushfire_prone_land: { label: 'Bushfire prone land', help: 'RFS-mapped Vegetation Category 1/2/3 or buffer.' },
  flood_planning: { label: 'Flood planning', help: 'Land subject to flood-planning controls.' },
  coastal_management: { label: 'Coastal management SEPP', help: 'Coastal Wetlands / Environment / Use / Vulnerability area.' },
  classified_road_frontage: {
    label: 'Classified road frontage',
    // Triggers SEPP T&I cl 2.119 (TfNSW concurrence + acoustic + access
    // restrictions) and Housing Code cl 3.8(b) 9 m front setback —
    // often disqualifies CDC on narrow lots.
    help: 'Boundary on a TfNSW-classified road (SP2). Triggers SEPP T&I cl 2.119: 9 m front setback for CDC, TfNSW concurrence on any DA, acoustic treatment, and no direct vehicle access from the road.',
  },
}

function renderValue(v: unknown): string {
  if (v == null) return '—'
  if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') return String(v)
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    const parts: string[] = []
    const labels: Record<string, string> = {
      min: 'min', ground: 'ground', first_floor: 'first floor', upper: 'upper',
      rear: 'rear', rear_upper: 'rear upper', side: 'side', side_upper: 'side upper',
      front: 'front',
    }
    for (const [k, v2] of Object.entries(o)) {
      if (typeof v2 === 'number' && labels[k]) parts.push(`${labels[k]} ${v2} m`)
    }
    if (parts.length) {
      // Append rule string when present (e.g. front-setback averaging
      // method alongside the fallback min metres). Without this the row
      // shows "min 4.5 m" with no hint that the cited rule is "average
      // of the three closest residential buildings".
      if (typeof o.rule === 'string' && o.rule.trim()) {
        return parts.join(', ') + ` (${o.rule.trim()})`
      }
      return parts.join(', ')
    }
    if (typeof o.rule === 'string' && o.rule.trim()) return o.rule.trim()
    return '—'   // never print [object Object] — bail to a dash
  }
  return String(v)
}

type YieldPathwayRow = {
  name: string
  gfa_m2: number | null
  formula: string
  citation: string | null
  inputs: Record<string, unknown>
  note: string | null
  breakdown?: string[]
  dwelling_count?: number | null
}

type Diagnostic = {
  severity: 'warning' | 'info'
  headline: string
  detail: string
}

type PermittedUseT = {
  permitted: boolean | null
  source: string
  citation: string | null
  reason: string
  needs_verification: boolean
}

function PermittedUseCard({
  form,
  result,
}: {
  form: string | undefined
  result: PermittedUseT | undefined
}) {
  if (!result) return null
  const formLabel = (form ?? '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  // 3 visual states: permitted (green), not permitted (red), unknown / needs
  // verification (amber). Verification flag overrides the green tint so a
  // Standard-Instrument default doesn't read as fully confirmed.
  let tone = 'border-flag-amber bg-massing-cream'
  let label = 'Verify with LEP land use table'
  if (result.permitted === true && !result.needs_verification) {
    tone = 'border-flag-green bg-massing-cream'
    label = 'Permitted'
  } else if (result.permitted === true) {
    tone = 'border-flag-amber bg-massing-cream'
    label = 'Likely permitted'
  } else if (result.permitted === false) {
    tone = 'border-massing-maroon bg-massing-maroon-soft'
    label = 'Not permitted'
  }
  return (
    <div className={`mb-4 px-4 py-3 border-l-4 ${tone}`}>
      <div className="flex items-baseline gap-2 mb-0.5">
        <span className="brand-eyebrow text-massing">Land-use check · {label}</span>
        <span className="text-[11px] text-massing-ink-light">· {formLabel}</span>
      </div>
      <div className="text-[12px] text-massing-ink-mid leading-relaxed">{result.reason}</div>
      {result.citation && (
        <div className="text-[11px] text-massing-ink-light italic mt-0.5">
          {result.citation} · source: {result.source}
        </div>
      )}
    </div>
  )
}

function YieldHero({
  siteAreaM2,
  maxGfaM2,
  bindingConstraint,
  pathways,
  diagnostics,
}: {
  siteAreaM2: number | null | undefined
  maxGfaM2: number | null | undefined
  bindingConstraint: string | null | undefined
  pathways: YieldPathwayRow[]
  diagnostics: Diagnostic[]
}) {
  // The hero: how much can the client build? Lead with the answer, then
  // show every pathway we considered so the reader can audit the
  // calculation and see which control is the bottleneck.
  const binding = pathways.find(
    (p) => p.gfa_m2 != null && Math.abs((p.gfa_m2 ?? 0) - (maxGfaM2 ?? 0)) < 0.5
  )
  const impliedFsr = (maxGfaM2 != null && siteAreaM2) ? maxGfaM2 / siteAreaM2 : null
  // For medium-density forms the binding pathway carries a dwelling
  // count. Show it on the hero so the planner sees "~4 dwellings @
  // ~120 m²" instead of just a bulk GFA number.
  const dwellings = binding?.dwelling_count ?? null
  const gfaPerDwelling = dwellings && maxGfaM2 ? Math.round(maxGfaM2 / dwellings) : null
  return (
    <div className="mb-8 rounded-lg glass overflow-hidden brand-gradient-ring">
      <div className="relative px-6 py-6 flex items-baseline justify-between flex-wrap gap-4">
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background:
              'radial-gradient(ellipse at 0% 100%, rgba(124,109,255,0.35), transparent 55%), radial-gradient(ellipse at 100% 0%, rgba(255,106,213,0.20), transparent 55%)',
          }}
        />
        <div className="relative">
          <div className="brand-eyebrow text-massing-ink-light mb-1">Council DA — maximum yield</div>
          <div className="font-serif text-5xl tracking-tight">
            <span className="brand-gradient-text">
              {maxGfaM2 != null ? `${Math.round(maxGfaM2).toLocaleString()} m²` : '—'}
            </span>
            {dwellings != null && (
              <span className="ml-3 text-2xl text-massing-ink-mid">· ~{dwellings} dwellings</span>
            )}
          </div>
          {siteAreaM2 != null && (
            <div className="text-sm text-massing-ink-mid mt-1">
              of buildable GFA on a {Math.round(siteAreaM2).toLocaleString()} m² site
              {impliedFsr != null && (
                <span> · implied FSR <strong className="text-massing-ink">{impliedFsr.toFixed(2)}:1</strong></span>
              )}
              {gfaPerDwelling != null && (
                <span> · ~{gfaPerDwelling} m²/dwelling</span>
              )}
            </div>
          )}
        </div>
        <div className="relative max-w-md">
          <div className="brand-eyebrow text-massing-ink-light mb-1">Binding constraint</div>
          <div className="text-sm text-massing-ink-mid">
            {binding ? <>{binding.name} — {binding.formula}</> : bindingConstraint ?? '—'}
          </div>
        </div>
      </div>
      {diagnostics.length > 0 && (
        <div className="surface-shade-strong">
          {diagnostics.map((d, i) => (
            <div
              key={i}
              className={`px-6 py-4 border-t-2 ${d.severity === 'warning' ? 'border-massing-maroon bg-massing-maroon-soft' : 'border-flag-amber bg-massing-cream'}`}
            >
              <div className="brand-eyebrow text-massing-ink mb-1">
                {d.severity === 'warning' ? '⚠ Sanity check' : 'Note'}
              </div>
              <div className="text-sm font-medium text-massing-ink">{d.headline}</div>
              <div className="text-[12px] text-massing-ink-mid italic mt-1 max-w-3xl leading-relaxed">{d.detail}</div>
            </div>
          ))}
        </div>
      )}
      {pathways.length > 0 && (
        <div className="px-6 py-5 bg-massing-cream">
          <div className="brand-eyebrow text-massing-ink-mid mb-3">How we got there</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="brand-eyebrow pb-2 pr-3">Pathway</th>
                <th className="brand-eyebrow pb-2 pr-3 text-right">Max GFA</th>
                <th className="brand-eyebrow pb-2 pr-3">Formula</th>
                <th className="brand-eyebrow pb-2 pr-3">Citation</th>
              </tr>
            </thead>
            <tbody>
              {pathways.map((p, i) => {
                const isBinding = binding && p.name === binding.name
                return (
                  <tr
                    key={i}
                    className={`border-t border-massing-rule-soft ${isBinding ? 'surface-shade-md' : ''}`}
                  >
                    <td className="py-2 pr-3 align-top">
                      <span className={isBinding ? 'font-medium text-massing-ink' : 'text-massing-ink'}>
                        {p.name}
                      </span>
                      {isBinding && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide brand-gradient-bg text-[#0a0c1a] px-1.5 py-0.5 rounded font-semibold">
                          binds
                        </span>
                      )}
                      {p.note && (
                        <div className="text-[11px] text-massing-ink-light italic mt-0.5">{p.note}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono align-top">
                      {p.gfa_m2 != null ? `${Math.round(p.gfa_m2).toLocaleString()} m²` : '—'}
                    </td>
                    <td className="py-2 pr-3 text-massing-ink-mid align-top">
                      <div>{p.formula}</div>
                      {p.breakdown && p.breakdown.length > 0 && (
                        <details className="mt-1.5">
                          <summary className="cursor-pointer text-[11px] text-massing hover:underline">
                            Show calculation
                          </summary>
                          <ul className="mt-1.5 space-y-1 list-none pl-0">
                            {p.breakdown.map((line, li) => (
                              <li
                                key={li}
                                className="text-[11px] text-massing-ink-mid font-mono border-l-2 border-massing-rule pl-2 py-0.5 whitespace-pre-wrap"
                              >
                                {line}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-[11px] text-massing-ink-light align-top">
                      {p.citation ?? '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <p className="text-[11px] text-massing-ink-light italic mt-3">
            The smallest pathway is the binding constraint. Storey-based pathways
            assume the storeys you supplied on the form (or 2 if left blank).
            Numbers are upper bounds — actual achievable GFA depends on design,
            articulation, and council discretion.
          </p>
        </div>
      )}
    </div>
  )
}

function DcpControlsTable({
  controls,
  dcpName,
  extras,
  lga,
  zone,
}: {
  controls: Record<string, ValueT>
  dcpName: string | null
  extras: any[] | undefined
  lga: string | null
  zone: string | undefined
}) {
  const entries = Object.entries(controls)
  const isPopulated = (v: ValueT) =>
    v.value !== null && v.value !== undefined && v.value !== ''
  const populated = entries.filter(([, v]) => isPopulated(v))
  const missingCount = entries.length - populated.length
  // "DCP not parsed" = every canonical field is silent AND there are no
  // extras. That tells the user the AI hasn't ingested this LGA's DCP
  // yet, and explains why Section C is mostly empty.
  const dcpEmpty = populated.length === 0 && (!extras || extras.length === 0)

  if (dcpEmpty) {
    return (
      <div className="border border-massing-rule bg-massing-cream p-4 text-sm">
        <div className="font-medium text-massing-ink mb-1">DCP not yet parsed for this LGA</div>
        <p className="text-massing-ink-mid text-[13px] leading-relaxed">
          {dcpName ? <>The cache row exists (<em>{dcpName}</em>) but it has no control data.</> : <>No DCP data has been ingested for this council yet.</>} Council DA controls are unavailable
          until an administrator triggers <code className="text-[11px] bg-massing-paper-deep px-1 py-0.5 rounded font-mono">POST /api/admin/parse-dcp?lga={lga}&amp;key=…</code>.
          The envelope figures below (if any) fall back to NSW metro defaults — verify
          before relying.
        </p>
      </div>
    )
  }

  return (
    <>
      <table className="w-full text-sm">
        <tbody>
          {populated.map(([k, v]) => (
            <tr key={k} className="border-t border-massing-rule-soft">
              <td className="py-2 pr-3">
                <div>{CONTROL_LABELS[k]?.label ?? k.replaceAll('_', ' ')}</div>
                {CONTROL_LABELS[k]?.help && (
                  <div className="text-[11px] text-massing-ink-light mt-0.5">
                    {CONTROL_LABELS[k].help}
                  </div>
                )}
              </td>
              <td className="py-2">
                <ValueCell
                  v={v}
                  unit={CONTROL_LABELS[k]?.unit}
                  label={CONTROL_LABELS[k]?.label ?? k.replaceAll('_', ' ')}
                  overrideContext={lga ? { target_type: 'dcp', lga_slug: lga, zone, housing_form: 'dwelling_house' } : undefined}
                  fieldKey={k}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {missingCount > 0 && (
        <p className="text-[11px] text-massing-ink-light mt-3 italic">
          {missingCount} other canonical control{missingCount === 1 ? ' is' : 's are'} not
          addressed by this DCP — see Council-specific controls below for council-unique
          rules captured during review.
        </p>
      )}
    </>
  )
}

function CopyExtraClauseButton({ text }: { text: string }) {
  // Lightweight copy button for the Council-specific controls subsection.
  // ValueCell has its own equivalent; this one is decoupled because
  // extras aren't ValueT instances.
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      title={`Copy "${text}"`}
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      className="text-massing hover:underline"
    >
      {copied ? '✓ copied' : 'Copy'}
    </button>
  )
}

function fmt(v: any, unit?: string): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'number') return unit ? `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${unit}` : `${v}`
  return String(v)
}
